import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';
import XavvyFormRenderer from './XavvyFormRenderer';
import logo from './assets/logo.svg';
import LayoutRenderer from './case-client/LayoutRenderer';
import { bootstrapPluginWidgets } from './case-client/pluginWidgets';
import PredictedNextChip from './PredictedNextChip';

// ─── Style tokens (mirrors CaseManager.jsx for visual consistency) ─────────
const containerStyle = { display: 'flex', flexDirection: 'column', height: '100vh', background: 'var(--bg-primary)', color: 'var(--text-primary)' };
const headerStyle = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 18px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)', flexWrap: 'wrap', gap: '8px', flexShrink: 0 };
const subBarStyle = { display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 18px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)', flexShrink: 0, flexWrap: 'wrap' };
const btnPrimary = { background: 'var(--accent-color)', color: '#fff', border: 'none', borderRadius: '6px', padding: '7px 14px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' };
const btnSecondary = { background: 'var(--bg-tertiary)', color: 'var(--text-primary)', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '7px 14px', cursor: 'pointer', fontSize: '13px' };
const btnDanger = { background: '#dc262620', color: '#ef4444', border: '1px solid #ef444450', borderRadius: '6px', padding: '7px 14px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 };
const inputStyle = { padding: '7px 10px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '13px', outline: 'none' };
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalContent = { background: 'var(--bg-secondary)', borderRadius: '12px', padding: '20px', width: '460px', maxWidth: '95vw', maxHeight: '85vh', overflow: 'auto', border: '1px solid var(--border-color)' };

const priorityColors = { critical: '#ef4444', high: '#f59e0b', medium: '#3b82f6', low: '#6b7280' };
const statusColors = { open: '#3b82f6', in_progress: '#f59e0b', under_review: '#8b5cf6', resolved: '#10b981', closed: '#6b7280', escalated: '#ef4444' };

export function Badge({ children, color }) {
  return <span style={{ background: `${color}20`, color, padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{children}</span>;
}

export function StatusBadge({ status }) {
  const c = statusColors[status] || '#6b7280';
  return <Badge color={c}>{(status || '').replace(/_/g, ' ')}</Badge>;
}

export function PriorityBadge({ priority }) {
  const c = priorityColors[priority] || '#6b7280';
  return <Badge color={c}>{priority || 'none'}</Badge>;
}

export function authHeaders(token) {
  return { Authorization: `Bearer ${token}` };
}

// Walks a xavvy form definition (which can nest fields under elements/columns/tabs/etc.)
// and returns a flat list of every named field. Mirrors the helper in CaseManager.jsx.
export function collectFormFields(formDef) {
  const out = [];
  const walk = (node) => {
    if (Array.isArray(node)) { node.forEach(walk); return; }
    if (!node || typeof node !== 'object') return;
    if (typeof node.name === 'string' && node.name) out.push(node);
    ['elements', 'fields', 'columns', 'tabs', 'children', 'items'].forEach(k => {
      if (k in node) walk(node[k]);
    });
  };
  walk(formDef?.elements || []);
  return out;
}

// Prefill: map case custom_fields values → form field names, honoring per-field
// case_field_key / task_field_key / no_writeback set in the form builder.
export function mapCaseValuesToFormNames(caseFields, formDef) {
  if (!caseFields || !formDef) return caseFields || {};
  const fields = collectFormFields(formDef);
  const cfLower = {};
  Object.entries(caseFields).forEach(([k, v]) => { cfLower[k.toLowerCase()] = v; });
  const result = {};
  fields.forEach(f => {
    if (f.no_writeback) return;
    if ((f.task_field_key || '').trim()) return;
    const caseKey = (f.case_field_key || '').trim() || f.name;
    if (caseFields[caseKey] !== undefined) result[f.name] = caseFields[caseKey];
    else if (cfLower[caseKey.toLowerCase()] !== undefined) result[f.name] = cfLower[caseKey.toLowerCase()];
  });
  Object.entries(caseFields).forEach(([k, v]) => {
    if (result[k] === undefined && !fields.some(f => f.name === k)) result[k] = v;
  });
  return result;
}

// Writeback: map form values → case custom_fields keys, using the picker's
// case_field_key when set; falls back to schema key by case-insensitive match.
export function mapFormValuesToCaseKeys(formValues, caseSchema, formDef) {
  if (!formValues) return {};
  const fields = formDef ? collectFormFields(formDef) : [];
  const fieldByName = {};
  fields.forEach(f => { fieldByName[f.name] = f; });
  const schemaLower = {};
  (caseSchema?.fields || []).forEach(f => { schemaLower[f.key.toLowerCase()] = f.key; });
  const result = {};
  Object.entries(formValues).forEach(([formName, v]) => {
    const fdef = fieldByName[formName];
    if (fdef?.no_writeback) return;
    if ((fdef?.task_field_key || '').trim()) return;
    const explicitKey = (fdef?.case_field_key || '').trim();
    if (explicitKey) { result[explicitKey] = v; return; }
    result[schemaLower[formName.toLowerCase()] || formName] = v;
  });
  return result;
}

// ─── Role Picker ──────────────────────────────────────────────────────────
function RolePicker({ token, workspaceId, currentRoleId, onSelect, onClose }) {
  const [roles, setRoles] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setError('');
    axios.get(`${API_BASE_URL}/api/casematrix/me/case-roles`, {
      params: { workspace_id: workspaceId },
      headers: authHeaders(token),
    }).then(r => {
      if (!cancelled) setRoles(r.data?.items || []);
    }).catch(e => {
      if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load roles.');
    });
    return () => { cancelled = true; };
  }, [token, workspaceId]);

  const choose = async (assignmentId) => {
    setSubmitting(true);
    setError('');
    try {
      const r = await axios.put(
        `${API_BASE_URL}/api/casematrix/me/active-case-role`,
        { assignment_id: assignmentId },
        { headers: authHeaders(token) },
      );
      onSelect(r.data?.active || { assignment_id: assignmentId });
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not switch role.');
    } finally {
      setSubmitting(false);
    }
  };

  const clearRole = async () => {
    setSubmitting(true);
    setError('');
    try {
      await axios.delete(`${API_BASE_URL}/api/casematrix/me/active-case-role`, { headers: authHeaders(token) });
      onSelect(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not clear role.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalContent} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h3 style={{ margin: 0, fontSize: '16px' }}>Pick your role</h3>
          <button onClick={onClose} style={{ ...btnSecondary, padding: '4px 10px' }}>Close</button>
        </div>
        {error && <div style={{ background: '#ef444420', color: '#ef4444', padding: '8px 10px', borderRadius: '6px', marginBottom: '12px', fontSize: '12px' }}>{error}</div>}
        {roles === null ? (
          <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading…</div>
        ) : roles.length === 0 ? (
          <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>You don't have any active case roles in this workspace. Ask your administrator to assign one.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {roles.map(r => {
              const isActive = r.assignment_id === currentRoleId;
              return (
                <button
                  key={r.assignment_id}
                  disabled={submitting}
                  onClick={() => choose(r.assignment_id)}
                  style={{
                    textAlign: 'left',
                    padding: '12px 14px',
                    borderRadius: '8px',
                    border: `1px solid ${isActive ? 'var(--accent-color)' : 'var(--border-color)'}`,
                    background: isActive ? 'var(--accent-color)15' : 'var(--bg-tertiary)',
                    color: 'var(--text-primary)',
                    cursor: submitting ? 'wait' : 'pointer',
                    opacity: submitting ? 0.6 : 1,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {r.color && <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: r.color, flexShrink: 0 }} />}
                    <span style={{ fontWeight: 600, fontSize: '14px' }}>{r.role_name || 'Unnamed role'}</span>
                  </div>
                  {isActive && <div style={{ color: 'var(--accent-color)', fontSize: '11px', marginTop: '6px', fontWeight: 600 }}>● Currently active</div>}
                </button>
              );
            })}
            {currentRoleId && (
              <button onClick={clearRole} disabled={submitting} style={{ ...btnSecondary, marginTop: '4px' }}>
                Clear active role
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ICN-style data grid tokens ───────────────────────────────────────────
const gridTableStyle = { width: '100%', borderCollapse: 'collapse', fontSize: '13px', tableLayout: 'auto' };
const gridThStyle = {
  padding: '8px 12px', fontWeight: 600, textAlign: 'left',
  background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--border-color)',
  position: 'sticky', top: 0, zIndex: 1,
  fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em',
  color: 'var(--text-secondary)', whiteSpace: 'nowrap',
};
const gridTdStyle = { padding: '7px 12px', verticalAlign: 'middle', borderBottom: '1px solid var(--border-color)' };

// ─── ICN-style left navigation rail ───────────────────────────────────────
function LeftNavRail({ baskets, activeTabId, onOpenHome, onOpenBasket, onRefresh, docuforgeEnabled, onOpenDocuforge }) {
  // Personal first, then shared (matches IBM-BAW UserQueue/WorkQueue ordering).
  const sorted = [...(baskets || [])].sort((a, b) => {
    if (a.basket_type === b.basket_type) return 0;
    return a.basket_type === 'personal' ? -1 : 1;
  });
  return (
    <div style={{ width: '220px', borderRight: '1px solid var(--border-color)', background: 'var(--bg-secondary)', overflow: 'auto', flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ padding: '12px 14px 4px', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-secondary)' }}>Browse</div>
      <RailItem
        label="Cases"
        icon="📋"
        isActive={activeTabId === 'home'}
        onClick={onOpenHome}
      />
      {docuforgeEnabled && (
        <RailItem
          label="Docuforge"
          icon="📄"
          isActive={activeTabId === 'docuforge' || (activeTabId || '').startsWith('document:')}
          title="Intelligent document processing — review, correct, ingest"
          onClick={onOpenDocuforge}
        />
      )}
      {sorted.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 14px 4px' }}>
            <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-secondary)' }}>In-Baskets</span>
            {onRefresh && (
              <button onClick={onRefresh} title="Refresh counts" style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px', padding: 0 }}>↻</button>
            )}
          </div>
          {sorted.map(ib => {
            const isPersonal = ib.basket_type === 'personal';
            const icon = ib.icon && ib.icon !== 'inbox' ? ib.icon : (isPersonal ? '👤' : '📥');
            return (
              <RailItem
                key={ib.id}
                label={ib.name}
                icon={icon}
                count={ib.item_count}
                isActive={activeTabId === `inbox:${ib.id}`}
                title={isPersonal ? 'Personal — items assigned to you' : 'Shared team queue'}
                onClick={() => onOpenBasket(ib)}
              />
            );
          })}
        </>
      )}
    </div>
  );
}

function RailItem({ label, icon, count, isActive, title, onClick }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        textAlign: 'left',
        padding: '7px 14px',
        border: 'none',
        background: isActive ? 'var(--accent-color)18' : 'transparent',
        color: isActive ? 'var(--accent-color)' : 'var(--text-primary)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
        fontSize: '13px',
        fontWeight: isActive ? 600 : 400,
        borderLeft: `3px solid ${isActive ? 'var(--accent-color)' : 'transparent'}`,
      }}
    >
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{icon} {label}</span>
      {count !== undefined && count !== null && (
        <span style={{ fontSize: '11px', fontWeight: 600, padding: '1px 7px', borderRadius: '8px', background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', flexShrink: 0 }}>
          {count}
        </span>
      )}
    </button>
  );
}

// ─── Tab strip (closeable, MDI) ───────────────────────────────────────────
function TabStrip({ tabs, activeTabId, dirtyIds, onActivate, onClose }) {
  return (
    <div style={{ display: 'flex', alignItems: 'stretch', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)', overflowX: 'auto', flexShrink: 0 }}>
      {tabs.map(t => {
        const isActive = t.id === activeTabId;
        const isDirty = dirtyIds && dirtyIds.has(t.id);
        return (
          <div
            key={t.id}
            onClick={() => onActivate(t.id)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 10px 8px 14px',
              borderRight: '1px solid var(--border-color)',
              borderBottom: `2px solid ${isActive ? 'var(--accent-color)' : 'transparent'}`,
              background: isActive ? 'var(--bg-primary)' : 'transparent',
              color: isActive ? 'var(--text-primary)' : 'var(--text-secondary)',
              cursor: 'pointer',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              fontWeight: isActive ? 600 : 400,
              minWidth: 0,
              maxWidth: '240px',
              flexShrink: 0,
            }}
            title={isDirty ? `${t.label} (unsaved changes)` : t.label}
          >
            <span style={{ flexShrink: 0 }}>{t.icon}</span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {t.label}{isDirty && <span style={{ color: 'var(--accent-color)', marginLeft: '4px', fontWeight: 700 }}>*</span>}
            </span>
            {t.id !== 'home' && onClose && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (isDirty && !window.confirm(`"${t.label}" has unsaved changes. Close anyway?`)) return;
                  onClose(t.id);
                }}
                title="Close tab"
                style={{ background: 'transparent', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: '0 4px', fontSize: '14px', flexShrink: 0, lineHeight: 1 }}
              >×</button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Cases dashboard: KPI cards + opt-in charts ──────────────────────────
// Mirrors the CaseManager dashboard but drops anything that depends on
// case-level user assignment (My Open, Unassigned, Team Performance) — in
// the worker UX cases aren't assigned to users, only work items are.
export function CasesDashboard({ token, workspaceId }) {
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    axios.get(
      `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/dashboard`,
      { headers: authHeaders(token) },
    ).then(r => { if (!cancelled) setStats(r.data || {}); })
      .catch(() => { if (!cancelled) setStats({}); });
    return () => { cancelled = true; };
  }, [token, workspaceId]);

  useEffect(() => {
    if (!expanded || !workspaceId) return;
    let cancelled = false;
    axios.get(
      `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/analytics/trends?days=30`,
      { headers: authHeaders(token) },
    ).then(r => { if (!cancelled) setTrends(r.data); }).catch(() => {});
    return () => { cancelled = true; };
  }, [expanded, token, workspaceId]);

  if (!stats) return null;

  const slaRate = stats.sla_compliance?.rate_pct;
  const cards = [
    { label: 'Open', value: stats.open, color: '#3b82f6' },
    { label: 'In Progress', value: stats.in_progress, color: '#f59e0b' },
    { label: 'Resolved', value: stats.resolved, color: '#10b981' },
    { label: 'Overdue', value: stats.overdue, color: '#ef4444' },
    ...(slaRate != null ? [{ label: 'SLA Compliance', value: `${slaRate}%`, color: slaRate >= 90 ? '#10b981' : slaRate >= 70 ? '#f59e0b' : '#ef4444' }] : []),
  ];
  const trendBars = trends?.series?.slice(-14) || [];
  const maxCreated = Math.max(1, ...trendBars.map(d => d.created));
  const cardStyle = { background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px' };

  return (
    <div style={{ padding: '14px 16px 0' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px', marginBottom: '10px' }}>
        {cards.map(c => (
          <div key={c.label} style={{ ...cardStyle, borderLeft: `3px solid ${c.color}`, padding: '10px 12px' }}>
            <div style={{ fontSize: '22px', fontWeight: 700, color: c.color }}>{c.value ?? 0}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>{c.label}</div>
          </div>
        ))}
      </div>

      <button onClick={() => setExpanded(e => !e)}
        style={{ background: 'none', border: 'none', color: 'var(--accent-color)', cursor: 'pointer', fontSize: '12px', padding: '4px 0', marginBottom: '8px' }}>
        {expanded ? '▾ Hide Analytics' : '▸ Show Analytics'}
      </button>

      {expanded && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
          <div style={{ ...cardStyle, padding: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '10px', color: 'var(--text-secondary)' }}>Cases — Last 14 Days</div>
            {trendBars.length > 0 ? (
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '80px' }}>
                {trendBars.map((d, i) => (
                  <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }} title={`${d.date}\nCreated: ${d.created}\nResolved: ${d.resolved}`}>
                    <div style={{ width: '100%', maxWidth: '20px', background: '#3b82f6', borderRadius: '2px 2px 0 0', height: `${Math.max(2, (d.created / maxCreated) * 60)}px` }} />
                    <div style={{ width: '100%', maxWidth: '20px', background: '#10b981', borderRadius: '0 0 2px 2px', height: `${Math.max(1, (d.resolved / maxCreated) * 60)}px`, marginTop: '-1px' }} />
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ height: '80px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-tertiary)', fontSize: '12px' }}>Loading…</div>
            )}
            <div style={{ display: 'flex', gap: '12px', marginTop: '8px', fontSize: '10px', color: 'var(--text-tertiary)' }}>
              <span><span style={{ display: 'inline-block', width: '8px', height: '8px', background: '#3b82f6', borderRadius: '2px', marginRight: '4px' }} />Created</span>
              <span><span style={{ display: 'inline-block', width: '8px', height: '8px', background: '#10b981', borderRadius: '2px', marginRight: '4px' }} />Resolved</span>
            </div>
          </div>

          <div style={{ ...cardStyle, padding: '12px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '10px', color: 'var(--text-secondary)' }}>Open by Priority</div>
            {Object.entries(stats.by_priority || {}).map(([p, count]) => {
              const pColor = { critical: '#ef4444', high: '#f59e0b', medium: '#3b82f6', low: '#6b7280' }[p] || '#6b7280';
              const maxP = Math.max(1, ...Object.values(stats.by_priority || {}));
              return (
                <div key={p} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '11px', width: '60px', color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{p}</span>
                  <div style={{ flex: 1, height: '14px', background: 'var(--bg-tertiary)', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${(count / maxP) * 100}%`, background: pColor, borderRadius: '4px' }} />
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: pColor, minWidth: '24px', textAlign: 'right' }}>{count}</span>
                </div>
              );
            })}
            {Object.keys(stats.by_priority || {}).length === 0 && (
              <div style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>No open cases</div>
            )}
          </div>

          <div style={{ ...cardStyle, padding: '12px', gridColumn: '1 / -1' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '10px', color: 'var(--text-secondary)' }}>Open by Type</div>
            {(stats.by_type || []).map((t, i) => {
              const maxT = Math.max(1, ...(stats.by_type || []).map(x => x.count));
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '11px', flex: '0 0 140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-secondary)' }}>{t.name}</span>
                  <div style={{ flex: 1, height: '14px', background: 'var(--bg-tertiary)', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${(t.count / maxT) * 100}%`, background: 'var(--accent-color)', borderRadius: '4px' }} />
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: 'var(--accent-color)', minWidth: '24px', textAlign: 'right' }}>{t.count}</span>
                </div>
              );
            })}
            {(stats.by_type || []).length === 0 && (
              <div style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>No open cases</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Home tab: cases as a dense data grid ─────────────────────────────────
export function HomeTab({
  token, workspaceId,
  cases, loading, error, currentUser,
  search, setSearch, statusFilter, setStatusFilter,
  onRefresh, onOpenCase,
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <CasesDashboard token={token} workspaceId={workspaceId} />
      <div style={subBarStyle}>
        <input
          type="text"
          placeholder="Search cases…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle, flex: '1 1 200px', maxWidth: '320px' }}
        />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={inputStyle}>
          <option value="open,in_progress,under_review">Active</option>
          <option value="open">Open</option>
          <option value="in_progress">In progress</option>
          <option value="under_review">Under review</option>
          <option value="resolved,closed">Resolved/Closed</option>
          <option value="">All</option>
        </select>
        <button onClick={onRefresh} style={btnSecondary} disabled={loading}>{loading ? 'Refreshing…' : 'Refresh'}</button>
        <span style={{ marginLeft: 'auto', color: 'var(--text-secondary)', fontSize: '12px' }}>
          {cases.length} case{cases.length === 1 ? '' : 's'}
        </span>
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        {error ? (
          <div style={{ padding: '14px', color: '#ef4444', fontSize: '13px' }}>{error}</div>
        ) : loading && cases.length === 0 ? (
          <div style={{ padding: '14px', color: 'var(--text-secondary)', fontSize: '13px' }}>Loading…</div>
        ) : cases.length === 0 ? (
          <div style={{ padding: '14px', color: 'var(--text-secondary)', fontSize: '13px' }}>No cases match the current view.</div>
        ) : (
          <table style={gridTableStyle}>
            <thead>
              <tr>
                <th style={gridThStyle}>Case #</th>
                <th style={gridThStyle}>Title</th>
                <th style={gridThStyle}>Status</th>
                <th style={gridThStyle}>Priority</th>
                <th style={gridThStyle}>Stage</th>
                <th style={gridThStyle}>Created</th>
              </tr>
            </thead>
            <tbody>
              {cases.map(c => (
                <tr key={c.id} onClick={() => onOpenCase(c)} style={{ cursor: 'pointer' }}>
                  <td style={{ ...gridTdStyle, fontFamily: 'monospace', fontSize: '12px', color: 'var(--text-secondary)' }}>{c.case_number || c.id.slice(0, 8)}</td>
                  <td style={{ ...gridTdStyle, fontWeight: 500 }}>{c.title || '(untitled)'}</td>
                  <td style={gridTdStyle}><StatusBadge status={c.status} /></td>
                  <td style={gridTdStyle}><PriorityBadge priority={c.priority} /></td>
                  <td style={{ ...gridTdStyle, fontSize: '12px', color: 'var(--text-secondary)' }}>{c.current_stage_name || '—'}</td>
                  <td style={{ ...gridTdStyle, fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Inbox tab: in-basket items as a dense data grid ──────────────────────
function InboxTab({ token, workspaceId, basket, onOpenWorkItem }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  // Admin-configured runtime filter chip values, keyed by ui_filter.field.
  // Sent to the items endpoint as `ui_field_filters` so the worker can narrow
  // a queue (e.g. by case priority, by department) without needing a new
  // basket per slice.
  const [filterValues, setFilterValues] = useState({});
  // ui_filters config returned by the backend on the items response —
  // captured here so we can render the filter chips even when items=[].
  const [uiFilters, setUiFilters] = useState([]);
  // Built-in client-side filters (search/status/priority) — applied locally
  // on top of the server-side ui_field_filters so the worker has the same
  // baseline narrowing controls regardless of basket config.
  const [builtinFilters, setBuiltinFilters] = useState({ status: '', priority: '', search: '' });

  const load = useCallback(async () => {
    if (!basket) return;
    setError('');
    try {
      const params = { page: 1, page_size: 200 };
      const activeUi = Object.entries(filterValues)
        .filter(([, v]) => v !== '' && v !== null && v !== undefined)
        .map(([field, value]) => ({ field, value }));
      if (activeUi.length > 0) {
        params.ui_field_filters = JSON.stringify(activeUi);
      }
      const r = await axios.get(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/in-baskets/${basket.id}/items`,
        { headers: authHeaders(token), params },
      );
      setItems(r.data?.items || []);
      setUiFilters(Array.isArray(r.data?.ui_filters) ? r.data.ui_filters : []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load basket items.');
      setItems([]);
    }
  }, [token, workspaceId, basket, filterValues]);

  useEffect(() => { load(); }, [load]);

  // Reset filter state when switching baskets so a stale value from another
  // basket doesn't silently narrow the new queue.
  useEffect(() => {
    setFilterValues({});
    setBuiltinFilters({ status: '', priority: '', search: '' });
  }, [basket?.id]);

  const isPersonal = basket?.basket_type === 'personal';
  const headerIcon = basket?.icon && basket.icon !== 'inbox' ? basket.icon : (isPersonal ? '👤' : '📥');
  const hasActiveUiFilters = Object.values(filterValues).some(v => v !== '' && v !== null && v !== undefined);
  const hasActiveBuiltinFilters = builtinFilters.status || builtinFilters.priority || builtinFilters.search;
  const hasAnyActiveFilters = hasActiveUiFilters || hasActiveBuiltinFilters;

  // Client-side filtering for status / priority / search. Server-side
  // ui_field_filters already narrowed the response.
  const visibleItems = (() => {
    if (!Array.isArray(items)) return items;
    const searchLower = builtinFilters.search.toLowerCase();
    return items.filter(it => {
      if (builtinFilters.status && it.status !== builtinFilters.status) return false;
      if (builtinFilters.priority && it.priority !== builtinFilters.priority) return false;
      if (searchLower) {
        const hay = `${it.step_name || ''} ${it.case?.case_number || ''} ${it.case?.title || ''} ${it.claimed_by_name || ''}`.toLowerCase();
        if (!hay.includes(searchLower)) return false;
      }
      return true;
    });
  })();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={subBarStyle}>
        <span style={{ fontSize: '13px', fontWeight: 600 }}>{headerIcon} {basket?.name}</span>
        {basket?.description && (
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{basket.description}</span>
        )}
        <button onClick={load} style={btnSecondary}>Refresh</button>
        <span style={{ marginLeft: 'auto', color: 'var(--text-secondary)', fontSize: '12px' }}>
          {hasAnyActiveFilters && Array.isArray(items)
            ? `${visibleItems.length} of ${items.length}`
            : (items?.length ?? 0)} item{(visibleItems?.length ?? items?.length ?? 0) === 1 ? '' : 's'}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 18px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>Filters</span>
        <input
          value={builtinFilters.search}
          onChange={e => setBuiltinFilters(f => ({ ...f, search: e.target.value }))}
          placeholder="Search step, case #, title…"
          style={{ ...inputStyle, padding: '4px 8px', fontSize: '12px', width: '180px' }}
        />
        <select
          value={builtinFilters.status}
          onChange={e => setBuiltinFilters(f => ({ ...f, status: e.target.value }))}
          style={{ ...inputStyle, padding: '4px 8px', fontSize: '12px' }}
        >
          <option value="">All statuses</option>
          <option value="available">Available</option>
          <option value="claimed">Claimed</option>
          <option value="completed">Completed</option>
          <option value="skipped">Skipped</option>
        </select>
        <select
          value={builtinFilters.priority}
          onChange={e => setBuiltinFilters(f => ({ ...f, priority: e.target.value }))}
          style={{ ...inputStyle, padding: '4px 8px', fontSize: '12px' }}
        >
          <option value="">All priorities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        {uiFilters.map(uf => (
          <div key={uf.field} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <label style={{ fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{uf.label || uf.field}:</label>
            {Array.isArray(uf.options) && uf.options.length > 0 ? (
              <select
                value={filterValues[uf.field] || ''}
                onChange={e => setFilterValues(prev => ({ ...prev, [uf.field]: e.target.value }))}
                style={{ ...inputStyle, padding: '4px 8px', fontSize: '12px' }}
              >
                <option value="">All</option>
                {uf.options.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : (
              <input
                value={filterValues[uf.field] || ''}
                onChange={e => setFilterValues(prev => ({ ...prev, [uf.field]: e.target.value }))}
                placeholder={uf.label || uf.field}
                style={{ ...inputStyle, padding: '4px 8px', fontSize: '12px', width: '140px' }}
              />
            )}
          </div>
        ))}
        {hasAnyActiveFilters && (
          <button
            onClick={() => { setFilterValues({}); setBuiltinFilters({ status: '', priority: '', search: '' }); }}
            style={{ background: 'none', border: 'none', color: 'var(--accent-color)', cursor: 'pointer', fontSize: '12px', padding: '4px' }}
          >
            Clear filters
          </button>
        )}
      </div>
      <div style={{ flex: 1, overflow: 'auto' }}>
        {error ? (
          <div style={{ padding: '14px', color: '#ef4444', fontSize: '13px' }}>{error}</div>
        ) : items === null ? (
          <div style={{ padding: '14px', color: 'var(--text-secondary)', fontSize: '13px' }}>Loading…</div>
        ) : items.length === 0 ? (
          <div style={{ padding: '14px', color: 'var(--text-secondary)', fontSize: '13px' }}>No work items in this basket.</div>
        ) : visibleItems.length === 0 ? (
          <div style={{ padding: '14px', color: 'var(--text-secondary)', fontSize: '13px' }}>No work items match the current filters.</div>
        ) : (
          <table style={gridTableStyle}>
            <thead>
              <tr>
                <th style={gridThStyle}>Case #</th>
                <th style={gridThStyle}>Step</th>
                <th style={gridThStyle}>Priority</th>
                <th style={gridThStyle}>Status</th>
                <th style={gridThStyle}>SLA</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.map(it => (
                <tr
                  key={it.id}
                  onClick={() => onOpenWorkItem(
                    { id: it.id, step_name: it.step_name },
                    {
                      id: it.case_id || it.case?.id,
                      case_number: it.case?.case_number,
                      title: it.case?.title,
                    },
                  )}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ ...gridTdStyle, fontFamily: 'monospace', fontSize: '12px', color: 'var(--text-secondary)' }}>{it.case?.case_number || it._columns?.case_number || ''}</td>
                  <td style={{ ...gridTdStyle, fontWeight: 500 }}>{it.step_name || ''}</td>
                  <td style={gridTdStyle}><PriorityBadge priority={it.priority} /></td>
                  <td style={gridTdStyle}><Badge color={it.status === 'claimed' ? '#3b82f6' : '#22c55e'}>{it.status}</Badge></td>
                  <td style={{ ...gridTdStyle, fontSize: '12px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                    {it.sla_deadline ? new Date(it.sla_deadline).toLocaleString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Case tab: own row state + 30s poll, hosts the case-category Layout ──
//
// Owns the case-row data layer (initial fetch + 30s poll) and feeds the live
// row into LayoutRenderer as `case={caseRow}`. The default tree resolves to
// `case.builtin`, which delegates back to CaseDetail via renderBuiltin. When
// the workspace authors a custom layout from granular widgets (case.header,
// case.tasks, …) those widgets read the same live row through useLayoutCtx
// and stay in sync with the poll automatically.
function CaseTab({ token, workspaceId, caseId, initialCase, currentUser, role, roleId, onCaseUpdated, onOpenWorkItem, workItemRefreshNonce, onDirtyChange }) {
  const [caseRow, setCaseRow] = useState(initialCase || null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setError('');
    axios.get(
      `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}`,
      { headers: authHeaders(token) },
    ).then(r => {
      if (cancelled) return;
      setCaseRow(prev => prev ? { ...prev, ...r.data } : r.data);
      if (onCaseUpdated) onCaseUpdated(r.data);
    }).catch(e => {
      if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load case.');
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, workspaceId, token]);

  // 30s poll so external state changes (other workers, SLA tick) flow back
  useEffect(() => {
    if (!caseRow?.id) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await axios.get(
          `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}`,
          { headers: authHeaders(token) },
        );
        if (cancelled) return;
        setCaseRow(prev => prev ? { ...prev, ...r.data } : r.data);
        if (onCaseUpdated) onCaseUpdated(r.data);
      } catch { /* network blips are non-fatal */ }
    };
    const interval = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(interval); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseRow?.id, workspaceId, token]);

  if (error) {
    return <div style={{ padding: '20px', color: '#ef4444', fontSize: '13px' }}>{error}</div>;
  }
  if (!caseRow) {
    return <div style={{ padding: '20px', color: 'var(--text-secondary)', fontSize: '13px' }}>Loading case…</div>;
  }

  const handleCaseUpdated = (updated) => {
    setCaseRow(prev => (prev && prev.id === updated.id) ? { ...prev, ...updated } : prev);
    if (onCaseUpdated) onCaseUpdated(updated);
  };

  return (
    <LayoutRenderer
      category="case"
      token={token}
      workspaceId={workspaceId}
      currentUser={currentUser}
      role={role}
      caseTypeId={caseRow?.case_type_id || null}
      roleId={roleId || null}
      case={caseRow}
      callbacks={{
        onCaseUpdated: handleCaseUpdated,
        onOpenWorkItem,
        onDirtyChange,
        renderBuiltin: () => (
          <CaseDetail
            token={token}
            workspaceId={workspaceId}
            caseRow={caseRow}
            currentUser={currentUser}
            onCaseUpdated={handleCaseUpdated}
            onOpenWorkItem={onOpenWorkItem}
            workItemRefreshNonce={workItemRefreshNonce}
            onDirtyChange={onDirtyChange}
          />
        ),
      }}
    />
  );
}

// ─── Status changer ───────────────────────────────────────────────────────
export function StatusChanger({ token, workspaceId, caseRow, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const wf = caseRow?.case_type?.status_workflow_json || {};
  const allowed = (wf.transitions && wf.transitions[caseRow.status]) || [];
  if (!caseRow.case_type_id) return null;
  if (!allowed || allowed.length === 0) return null;

  const transition = async (next) => {
    setBusy(true);
    setError('');
    try {
      const r = await axios.post(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseRow.id}/status`,
        { status: next },
        { headers: authHeaders(token) },
      );
      onUpdated(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not transition.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
      <span style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)' }}>Move to</span>
      {allowed.map(s => (
        <button
          key={s}
          disabled={busy}
          onClick={() => transition(s)}
          style={{ ...btnSecondary, padding: '5px 10px', fontSize: '12px' }}
        >
          {s.replace(/_/g, ' ')}
        </button>
      ))}
      {error && <span style={{ color: '#ef4444', fontSize: '11px' }}>{error}</span>}
    </div>
  );
}

// ─── Comments tab ─────────────────────────────────────────────────────────
export function CommentsTab({ token, workspaceId, caseId }) {
  const [comments, setComments] = useState(null);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState('');
  const [internal, setInternal] = useState(true);
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    if (!caseId) return;
    setError('');
    try {
      const r = await axios.get(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/comments`,
        { headers: authHeaders(token) },
      );
      setComments(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load comments.');
      setComments([]);
    }
  }, [token, workspaceId, caseId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!draft.trim()) return;
    setPosting(true);
    setError('');
    try {
      await axios.post(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/comments`,
        { content: draft, is_internal: internal },
        { headers: authHeaders(token) },
      );
      setDraft('');
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not post comment.');
    } finally {
      setPosting(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          rows={3}
          placeholder="Add a comment. Use @username to mention."
          style={{ ...inputStyle, resize: 'vertical', minHeight: '64px', fontFamily: 'inherit' }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
            <input type="checkbox" checked={internal} onChange={e => setInternal(e.target.checked)} />
            Internal (not shown in portal)
          </label>
          <button onClick={submit} disabled={posting || !draft.trim()} style={btnPrimary}>
            {posting ? 'Posting…' : 'Post comment'}
          </button>
          {error && <span style={{ color: '#ef4444', fontSize: '12px' }}>{error}</span>}
        </div>
      </div>
      {comments === null ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading…</div>
      ) : comments.length === 0 ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>No comments yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {comments.map(c => (
            <div key={c.id} style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '10px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, fontSize: '12px' }}>{c.user_name || `User #${c.user_id}`}</span>
                {c.is_internal && <Badge color="#f59e0b">internal</Badge>}
                <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>
                  {c.created_at ? new Date(c.created_at).toLocaleString() : ''}
                </span>
              </div>
              <div style={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>{c.content}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Tasks tab ────────────────────────────────────────────────────────────
export function TasksTab({ token, workspaceId, caseId, currentUser, onWorkItemOpen }) {
  const [progress, setProgress] = useState(null);
  const [available, setAvailable] = useState([]);
  const [error, setError] = useState('');
  const [launching, setLaunching] = useState(null);
  const [launchModal, setLaunchModal] = useState(null); // { tt, formDef|null, values }

  const load = useCallback(async () => {
    if (!caseId) return;
    setError('');
    try {
      const [p, a] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/task-progress`, { headers: authHeaders(token) }),
        axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/available-tasks`, { headers: authHeaders(token) }).catch(() => ({ data: [] })),
      ]);
      setProgress(Array.isArray(p.data) ? p.data : []);
      setAvailable(Array.isArray(a.data) ? a.data : []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load tasks.');
      setProgress([]);
    }
  }, [token, workspaceId, caseId]);

  useEffect(() => { load(); }, [load]);

  const submitLaunch = async (ttId, formData) => {
    setLaunching(ttId);
    setError('');
    try {
      const body = { task_type_id: ttId };
      if (formData && Object.keys(formData).length > 0) body.form_data = formData;
      await axios.post(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/launch-task`,
        body,
        { headers: authHeaders(token) },
      );
      setLaunchModal(null);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not launch task.');
    } finally {
      setLaunching(null);
    }
  };

  const handleLaunchClick = async (tt) => {
    if (tt.launch_form_id) {
      try {
        const res = await axios.get(`${API_BASE_URL}/xavvy-forms/${tt.launch_form_id}`, { headers: authHeaders(token) });
        setLaunchModal({ tt, formDef: res.data?.definition || null, values: {} });
      } catch (e) {
        setError('Failed to load launch form.');
      }
      return;
    }
    const fields = (tt.schema_json && Array.isArray(tt.schema_json.fields)) ? tt.schema_json.fields : [];
    if (fields.length > 0) {
      setLaunchModal({ tt, formDef: null, values: {} });
      return;
    }
    submitLaunch(tt.id, null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      {error && <div style={{ background: '#ef444420', color: '#ef4444', padding: '8px 10px', borderRadius: '6px', fontSize: '12px' }}>{error}</div>}

      {/* Discretionary task palette */}
      {available.length > 0 && (
        <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '10px 12px' }}>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: '8px' }}>Launch a task</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {available.map(tt => (
              <button
                key={tt.id}
                onClick={() => handleLaunchClick(tt)}
                disabled={launching === tt.id || !tt.can_add}
                style={{ ...btnSecondary, fontSize: '12px' }}
                title={tt.cannot_add_reason || ''}
              >
                {launching === tt.id ? '…' : `+ ${tt.name}`}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Active tasks + their work items */}
      {progress === null ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading…</div>
      ) : progress.length === 0 ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>No tasks on this case yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {progress.map(t => {
            const stepCount = t.total_steps || 0;
            const done = t.completed_steps || 0;
            const wi = t.active_work_item;
            const isMine = wi && (wi.claimed_by === currentUser?.id);
            return (
              <div key={t.id} style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '13px', fontWeight: 600 }}>{t.task_type_name || t.title}</span>
                  <StatusBadge status={t.status} />
                  <PriorityBadge priority={t.priority} />
                  {stepCount > 0 && (
                    <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>
                      Step {done}/{stepCount}{t.current_step_name ? ` · ${t.current_step_name}` : ''}
                    </span>
                  )}
                </div>
                {t.description && <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{t.description}</div>}
                {wi && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                    <Badge color={isMine ? '#10b981' : (wi.status === 'claimed' ? '#f59e0b' : '#3b82f6')}>
                      {wi.status === 'claimed' ? (isMine ? 'claimed by you' : `claimed by user #${wi.claimed_by}`) : 'available'}
                    </Badge>
                    <button
                      onClick={() => onWorkItemOpen(wi.id, t.current_step_name || wi.step_name || t.task_type_name || t.title)}
                      style={{ ...btnPrimary, padding: '5px 10px', fontSize: '12px' }}
                    >
                      Open step
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {launchModal && (
        <LaunchTaskModal
          token={token}
          workspaceId={workspaceId}
          caseId={caseId}
          currentUser={currentUser}
          launchModal={launchModal}
          launching={launching}
          onCancel={() => setLaunchModal(null)}
          onChangeValues={(vals) => setLaunchModal(m => m ? { ...m, values: vals } : m)}
          onSubmit={() => {
            if (!launchModal.formDef) {
              const fields = launchModal.tt.schema_json?.fields || [];
              const missing = fields.filter(f => f.required && (launchModal.values[f.key] === undefined || launchModal.values[f.key] === '' || launchModal.values[f.key] === null));
              if (missing.length) {
                setError(`Missing required fields: ${missing.map(f => f.label || f.key).join(', ')}`);
                return;
              }
            }
            submitLaunch(launchModal.tt.id, launchModal.values);
          }}
        />
      )}
    </div>
  );
}

function LaunchTaskModal({ token, workspaceId, caseId, currentUser, launchModal, launching, onCancel, onChangeValues, onSubmit }) {
  const fields = launchModal.tt.schema_json?.fields || [];

  // Bundled draft for granular widgets — they read these slices from
  // useLayoutCtx().draft and write back through setDraft(). The modal owns
  // the actual values via launchModal.values + onChangeValues.
  const draft = {
    taskType: launchModal.tt,
    formDef: launchModal.formDef || null,
    values: launchModal.values || {},
    busy: launching === launchModal.tt.id,
  };

  const setDraft = (patch) => {
    const apply = (next) => {
      if (!next || typeof next !== 'object') return;
      if ('values' in next) onChangeValues(next.values);
    };
    if (typeof patch === 'function') apply(patch(draft));
    else apply(patch);
  };

  const builtinBody = (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'auto' }}>
      {launchModal.tt.description && (
        <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>{launchModal.tt.description}</div>
      )}
      {launchModal.formDef ? (
        <XavvyFormRenderer
          definition={launchModal.formDef}
          initialValues={launchModal.values}
          onValuesChange={onChangeValues}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {fields.map(f => (
            <SimpleField
              key={f.key}
              field={f}
              value={launchModal.values[f.key]}
              onChange={(v) => onChangeValues({ ...launchModal.values, [f.key]: v })}
            />
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '14px', paddingTop: '12px', borderTop: '1px solid var(--border-color)' }}>
        <button onClick={onCancel} style={btnSecondary}>Cancel</button>
        <button onClick={onSubmit} disabled={launching === launchModal.tt.id} style={btnPrimary}>
          {launching === launchModal.tt.id ? 'Launching…' : 'Launch'}
        </button>
      </div>
    </div>
  );

  return (
    <div style={modalOverlay} onClick={onCancel}>
      <div style={{ ...modalContent, width: '600px', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h3 style={{ margin: 0, fontSize: '16px' }}>{launchModal.tt.name}</h3>
          <button onClick={onCancel} style={{ ...btnSecondary, padding: '4px 10px' }}>×</button>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: '0 1 auto', maxHeight: '60vh' }}>
          <LayoutRenderer
            category="add_task"
            token={token}
            workspaceId={workspaceId}
            currentUser={currentUser}
            taskTypeId={launchModal.tt.id}
            case={caseId ? { id: caseId } : null}
            draft={draft}
            setDraft={setDraft}
            callbacks={{
              onSubmit,
              onCancel,
              renderBuiltin: () => builtinBody,
            }}
          />
        </div>
      </div>
    </div>
  );
}

export function SimpleField({ field, value, onChange }) {
  const t = (field.type || '').toLowerCase();
  const inputType =
    t === 'number' || t === 'integer' || t === 'float' ? 'number' :
    t === 'date' ? 'date' :
    t === 'datetime' || t === 'datetime-local' ? 'datetime-local' :
    t === 'time' ? 'time' :
    t === 'email' ? 'email' :
    t === 'url' ? 'url' :
    t === 'password' ? 'password' :
    t === 'tel' || t === 'phone' ? 'tel' : 'text';
  return (
    <div>
      <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>
        {field.label || field.key}{field.required && <span style={{ color: '#ef4444' }}> *</span>}
      </label>
      {t === 'textarea' ? (
        <textarea value={value || ''} onChange={e => onChange(e.target.value)} style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }} placeholder={field.placeholder} />
      ) : t === 'select' ? (
        <select value={value || ''} onChange={e => onChange(e.target.value)} style={{ ...inputStyle, width: '100%' }}>
          <option value="">Select…</option>
          {(field.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      ) : t === 'boolean' || t === 'checkbox' ? (
        <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} />
      ) : (
        <input type={inputType} value={value ?? ''} onChange={e => onChange(e.target.value)} style={{ ...inputStyle, width: '100%' }} placeholder={field.placeholder} />
      )}
    </div>
  );
}

// ─── Activity tab ─────────────────────────────────────────────────────────
export function ActivityTab({ token, workspaceId, caseId }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    setError('');
    setItems(null);
    axios.get(
      `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseId}/activity`,
      { headers: authHeaders(token), params: { page_size: 100 } },
    ).then(r => {
      if (!cancelled) setItems(r.data?.items || []);
    }).catch(e => {
      if (!cancelled) {
        setError(e?.response?.data?.detail || 'Failed to load activity.');
        setItems([]);
      }
    });
    return () => { cancelled = true; };
  }, [token, workspaceId, caseId]);

  if (error) return <div style={{ color: '#ef4444', fontSize: '13px' }}>{error}</div>;
  if (items === null) return <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading…</div>;
  if (items.length === 0) return <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>No activity yet.</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {items.map(a => (
        <div key={a.id} style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderRadius: '8px', padding: '8px 10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <Badge color="#8b5cf6">{a.action_type}</Badge>
            <span style={{ fontSize: '12px' }}>{a.user_name || (a.user_id ? `User #${a.user_id}` : 'system')}</span>
            <span style={{ color: 'var(--text-secondary)', fontSize: '11px' }}>
              {a.created_at ? new Date(a.created_at).toLocaleString() : ''}
            </span>
          </div>
          {(a.old_value || a.new_value) && (
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>
              {a.old_value !== undefined && a.old_value !== null && <>from <code>{JSON.stringify(a.old_value)}</code> </>}
              {a.new_value !== undefined && a.new_value !== null && <>to <code>{JSON.stringify(a.new_value)}</code></>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Work Item Tab — full-pane step view with case context sidebar ───────
//
// Replaces the old `WorkItemModal`. Layout:
//   ┌── sub-bar: 📁 CASE-001 (link) · Step name · status badges · claim/release ─┐
//   ├──────────────────────────────────────────┬───────────────────────────────┤
//   │ Instructions                             │ Case context                  │
//   │ Form (XavvyFormRenderer or inline)       │  Title / status / priority    │
//   │                                          │  Custom fields                │
//   │                                          │  [Open full case]             │
//   ├──────────────────────────────────────────┴───────────────────────────────┤
//   │                       (footer)            [decision buttons / Submit]    │
//   └─────────────────────────────────────────────────────────────────────────┘
function WorkItemTab({ token, workspaceId, workItemId, caseHint, currentUser, role, roleId, onCompleted, onOpenCase, onDirtyChange }) {
  const [wi, setWi] = useState(null);
  const [caseRow, setCaseRow] = useState(caseHint || null);
  const [values, setValues] = useState({});
  // Snapshot of `values` representing the last persisted state — initial draft
  // from response_data_json, then re-snapshotted after each successful Save.
  // Drives the "*" tab indicator: dirty = JSON(values) !== JSON(savedSnapshot).
  const [savedSnapshot, setSavedSnapshot] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setError('');
    axios.get(
      `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/work-items/${workItemId}`,
      { headers: authHeaders(token) },
    ).then(r => {
      if (cancelled) return;
      setWi(r.data);
      // Pre-fill from previously saved draft (response_data_json).
      let prior = {};
      try {
        const raw = r.data?.response_data_json;
        if (raw) prior = typeof raw === 'string' ? JSON.parse(raw) : raw;
      } catch { prior = {}; }
      const init = prior && typeof prior === 'object' ? prior : {};
      setValues(init);
      setSavedSnapshot(init);
      setSavedAt(null);
    }).catch(e => {
      if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load step.');
    });
    return () => { cancelled = true; };
  }, [token, workspaceId, workItemId]);

  // Notify parent of dirty state (drives "*" on the tab strip + close confirm).
  useEffect(() => {
    if (!onDirtyChange) return;
    onDirtyChange(JSON.stringify(values) !== JSON.stringify(savedSnapshot));
  }, [values, savedSnapshot, onDirtyChange]);

  useEffect(() => {
    return () => { if (onDirtyChange) onDirtyChange(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load full case row for the sidebar (independent of the WI fetch — we
  // start with the lightweight `caseHint` from the inbox row click and
  // upgrade to the full row once the GET resolves).
  useEffect(() => {
    const cid = wi?.case_id || caseHint?.id;
    if (!cid) return;
    let cancelled = false;
    axios.get(
      `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${cid}`,
      { headers: authHeaders(token) },
    ).then(r => {
      if (!cancelled) setCaseRow(prev => ({ ...(prev || {}), ...r.data }));
    }).catch(() => { /* sidebar falls back to caseHint */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wi?.case_id, caseHint?.id, workspaceId, token]);

  const claim = async () => {
    setBusy(true); setError('');
    try {
      const r = await axios.post(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/work-items/${workItemId}/claim`,
        {},
        { headers: authHeaders(token) },
      );
      setWi(prev => ({ ...prev, ...r.data }));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not claim.');
    } finally { setBusy(false); }
  };

  const release = async () => {
    setBusy(true); setError('');
    try {
      const r = await axios.post(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/work-items/${workItemId}/release`,
        {},
        { headers: authHeaders(token) },
      );
      setWi(prev => ({ ...prev, ...r.data }));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not release.');
    } finally { setBusy(false); }
  };

  const complete = async (decision) => {
    setBusy(true); setError('');
    try {
      await axios.post(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/work-items/${workItemId}/complete`,
        { fields: values, decision: decision || 'submit' },
        { headers: authHeaders(token) },
      );
      onCompleted && onCompleted();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.message) {
        setError(detail.message + (detail.errors ? `: ${JSON.stringify(detail.errors)}` : ''));
      } else {
        setError(typeof detail === 'string' ? detail : 'Could not complete.');
      }
    } finally { setBusy(false); }
  };

  const saveDraft = async () => {
    setBusy(true); setError('');
    // Snapshot the values shipped to the server so the dirty check clears
    // even if the user keeps typing while the request is in flight.
    const snap = values;
    try {
      await axios.post(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/work-items/${workItemId}/save`,
        { fields: snap },
        { headers: authHeaders(token) },
      );
      setSavedSnapshot(snap);
      setSavedAt(new Date());
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not save.');
    } finally { setBusy(false); }
  };

  if (!wi && error) {
    return <div style={{ padding: '20px', color: '#ef4444', fontSize: '13px' }}>{error}</div>;
  }
  if (!wi) {
    return <div style={{ padding: '20px', color: 'var(--text-secondary)', fontSize: '13px' }}>Loading step…</div>;
  }

  const isMine = wi.claimed_by === currentUser?.id;
  const canSubmit = wi.status === 'claimed' && isMine;
  const decisions = (() => {
    try { return wi.decisions_json ? (typeof wi.decisions_json === 'string' ? JSON.parse(wi.decisions_json) : wi.decisions_json) : null; } catch { return null; }
  })();
  // Backend stores inline_fields_json as a bare JSON array (json.dumps of the
  // node's `fields` param). Defensive against a wrapped {fields:[...]} shape.
  const inlineFieldsArray = (() => {
    try {
      const raw = wi.inline_fields_json
        ? (typeof wi.inline_fields_json === 'string' ? JSON.parse(wi.inline_fields_json) : wi.inline_fields_json)
        : null;
      if (!raw) return [];
      if (Array.isArray(raw)) return raw;
      if (Array.isArray(raw.fields)) return raw.fields;
      return [];
    } catch { return []; }
  })();

  const caseRef = caseRow || caseHint || (wi.case_id ? { id: wi.case_id } : null);
  const caseLabel = caseRef ? (caseRef.case_number || (caseRef.id ? caseRef.id.slice(0, 8) : 'case')) : null;

  const builtinView = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Header sub-bar */}
      <div style={subBarStyle}>
        {caseRef && onOpenCase && (
          <button
            onClick={() => onOpenCase(caseRef)}
            style={{ ...btnSecondary, padding: '4px 10px', fontFamily: 'monospace', fontSize: '12px' }}
            title="Open full case"
          >
            📁 {caseLabel}
          </button>
        )}
        <span style={{ fontWeight: 600, fontSize: '14px' }}>{wi.step_name || 'Step'}</span>
        <Badge color={wi.status === 'completed' ? '#10b981' : wi.status === 'claimed' ? '#f59e0b' : '#3b82f6'}>{wi.status}</Badge>
        {wi.role_name && <Badge color="#8b5cf6">role: {wi.role_name}</Badge>}
        {wi.sla_deadline && (
          <span style={{ fontSize: '12px', color: new Date(wi.sla_deadline) < new Date() ? '#ef4444' : 'var(--text-secondary)' }}>
            SLA: {new Date(wi.sla_deadline).toLocaleString()}
          </span>
        )}
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          {wi.status === 'available' && <button onClick={claim} disabled={busy} style={btnPrimary}>Claim step</button>}
          {wi.status === 'claimed' && isMine && <button onClick={release} disabled={busy} style={btnSecondary}>Release</button>}
          {wi.status === 'claimed' && !isMine && (
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              Claimed by {wi.claimed_by_name || `user #${wi.claimed_by}`}
            </span>
          )}
        </span>
      </div>

      {/* Body: form + case context sidebar */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
          {wi.step_description && (
            <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
              {wi.step_description}
            </p>
          )}
          {wi.step_instructions && (
            <div style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-color)', borderLeft: '3px solid var(--accent-color)', borderRadius: '6px', padding: '10px 12px', fontSize: '13px', marginBottom: '14px', whiteSpace: 'pre-wrap' }}>
              {wi.step_instructions}
            </div>
          )}
          {wi.form_definition ? (
            <XavvyFormRenderer
              token={token}
              definition={wi.form_definition}
              initialValues={values}
              readOnly={!canSubmit}
              onValuesChange={setValues}
              actorRoles={wi.actor_role_names || []}
            />
          ) : inlineFieldsArray.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {inlineFieldsArray.map(f => (
                <SimpleField
                  key={f.key || f.name}
                  field={{ ...f, key: f.key || f.name }}
                  value={values[f.key || f.name]}
                  onChange={(v) => setValues(prev => ({ ...prev, [f.key || f.name]: v }))}
                />
              ))}
            </div>
          ) : (
            <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>This step requires no input — submit to advance.</div>
          )}
          {error && (
            <div style={{ background: '#ef444420', color: '#ef4444', padding: '8px 10px', borderRadius: '6px', marginTop: '12px', fontSize: '12px' }}>{error}</div>
          )}
        </div>
        <CaseContextSidebar caseRow={caseRef} onOpenCase={onOpenCase} />
      </div>

      {/* Footer */}
      <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border-color)', background: 'var(--bg-secondary)', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '8px', flexWrap: 'wrap', flexShrink: 0 }}>
        {canSubmit ? (
          <>
            {savedAt && (
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontStyle: 'italic', marginRight: 'auto' }}>
                Saved at {savedAt.toLocaleTimeString()}
              </span>
            )}
            <button disabled={busy} onClick={saveDraft} style={btnSecondary} title="Save form values without completing the step">
              Save
            </button>
            {decisions && Array.isArray(decisions) && decisions.length > 0 ? (
              decisions.map(d => (
                <button
                  key={d.value}
                  disabled={busy}
                  onClick={() => complete(d.value)}
                  style={{
                    ...btnPrimary,
                    background: d.style === 'danger' ? '#ef4444' : d.style === 'success' ? '#10b981' : 'var(--accent-color)',
                  }}
                >
                  {d.label || d.value}
                </button>
              ))
            ) : (
              <button disabled={busy} onClick={() => complete('submit')} style={btnPrimary}>
                Complete Step
              </button>
            )}
          </>
        ) : wi.status === 'available' ? (
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
            Claim this step above to enable actions.
          </span>
        ) : wi.status === 'claimed' && !isMine ? (
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
            {wi.claimed_by_name ? `${wi.claimed_by_name} is currently working on this step.` : 'Another worker is currently on this step.'}
          </span>
        ) : (
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>
            This step is {wi.status}.
          </span>
        )}
      </div>
    </div>
  );

  return (
    <LayoutRenderer
      category="task"
      token={token}
      workspaceId={workspaceId}
      currentUser={currentUser}
      role={role}
      roleId={roleId || null}
      stepName={wi?.step_name || null}
      taskTypeId={wi?.task_type_id || null}
      case={caseRef}
      workItem={wi}
      callbacks={{
        onOpenCase,
        onCompleted,
        onDirtyChange,
        renderBuiltin: () => builtinView,
      }}
    />
  );
}

export function CaseContextSidebar({ caseRow, onOpenCase }) {
  if (!caseRow || (!caseRow.title && !caseRow.case_number)) {
    return (
      <div style={{ width: '300px', borderLeft: '1px solid var(--border-color)', background: 'var(--bg-secondary)', padding: '14px', color: 'var(--text-secondary)', fontSize: '12px', flexShrink: 0 }}>
        Loading case context…
      </div>
    );
  }
  const cf = (() => {
    try {
      if (!caseRow.custom_fields_json) return null;
      return typeof caseRow.custom_fields_json === 'string' ? JSON.parse(caseRow.custom_fields_json) : caseRow.custom_fields_json;
    } catch { return null; }
  })();
  return (
    <div style={{ width: '300px', borderLeft: '1px solid var(--border-color)', background: 'var(--bg-secondary)', padding: '14px', overflow: 'auto', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-secondary)' }}>Case</div>
      <div>
        <div style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--text-secondary)' }}>{caseRow.case_number || caseRow.id}</div>
        <div style={{ fontSize: '14px', fontWeight: 600, marginTop: '2px' }}>{caseRow.title || '(untitled)'}</div>
      </div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
        {caseRow.status && <StatusBadge status={caseRow.status} />}
        {caseRow.priority && <PriorityBadge priority={caseRow.priority} />}
        {caseRow.case_type?.name && <Badge color="#8b5cf6">{caseRow.case_type.icon || '📋'} {caseRow.case_type.name}</Badge>}
      </div>
      {caseRow.description && (
        <div>
          <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-secondary)', marginBottom: '4px' }}>Description</div>
          <div style={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>{caseRow.description}</div>
        </div>
      )}
      {cf && Object.keys(cf).filter(k => !k.startsWith('_')).length > 0 && (
        <div>
          <div style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-secondary)', marginBottom: '6px' }}>Custom fields</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px' }}>
            {Object.entries(cf).filter(([k]) => !k.startsWith('_')).map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ color: 'var(--text-secondary)' }}>{k}</span>
                <span style={{ wordBreak: 'break-word', textAlign: 'right' }}>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {onOpenCase && (
        <button onClick={() => onOpenCase(caseRow)} style={{ ...btnSecondary, marginTop: 'auto' }}>
          Open full case →
        </button>
      )}
    </div>
  );
}

// ─── Add Case wizard (Phase 4) ───────────────────────────────────────────
function AddCaseModal({ token, workspaceId, currentUser, role, roleId, onClose, onCreated }) {
  const [types, setTypes] = useState(null);
  const [picked, setPicked] = useState(null);
  // When the picked case type has form_config_json.intake_form_id set, we
  // collect values keyed by form field NAME (XavvyFormRenderer's API). On
  // submit we map them to case custom-field KEYS via mapFormValuesToCaseKeys.
  // When no intake form is configured we fall back to the schema-driven
  // SimpleField loop, keyed by schema field key.
  const [form, setForm] = useState({ title: '', description: '', priority: 'medium', custom_fields: {} });
  const [intakeFormDef, setIntakeFormDef] = useState(null);
  const [intakeFormValues, setIntakeFormValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Bundled draft for granular widgets — they read these slices via
  // useLayoutCtx().draft and write back via useLayoutCtx().setDraft({...patch}).
  const draft = useMemo(() => ({
    picked,
    form,
    intakeFormDef,
    intakeFormValues,
    busy,
    error,
  }), [picked, form, intakeFormDef, intakeFormValues, busy, error]);

  const setDraft = useCallback((patch) => {
    const apply = (next) => {
      if (!next || typeof next !== 'object') return;
      if ('form' in next) setForm(next.form);
      if ('intakeFormValues' in next) setIntakeFormValues(next.intakeFormValues);
    };
    if (typeof patch === 'function') apply(patch(draft));
    else apply(patch);
  }, [draft]);

  useEffect(() => {
    let cancelled = false;
    axios.get(
      `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-types`,
      { headers: authHeaders(token) },
    ).then(r => {
      if (!cancelled) setTypes(Array.isArray(r.data) ? r.data.filter(t => t.is_active !== false) : []);
    }).catch(e => {
      if (!cancelled) {
        setError(e?.response?.data?.detail || 'Failed to load case types.');
        setTypes([]);
      }
    });
    return () => { cancelled = true; };
  }, [token, workspaceId]);

  // Load intake form when a type with form_config_json.intake_form_id is picked.
  useEffect(() => {
    if (!picked) { setIntakeFormDef(null); setIntakeFormValues({}); return; }
    const cfg = picked.form_config_json;
    const parsedCfg = typeof cfg === 'string'
      ? (() => { try { return JSON.parse(cfg); } catch { return null; } })()
      : cfg;
    const intakeId = parsedCfg?.intake_form_id;
    if (!intakeId) { setIntakeFormDef(null); setIntakeFormValues({}); return; }
    let cancelled = false;
    axios.get(`${API_BASE_URL}/xavvy-forms/${intakeId}`, { headers: authHeaders(token) })
      .then(r => {
        if (cancelled) return;
        const def = r.data?.definition;
        const parsed = typeof def === 'string' ? (() => { try { return JSON.parse(def); } catch { return null; } })() : def;
        setIntakeFormDef(parsed || null);
        setIntakeFormValues({});
      })
      .catch(() => { if (!cancelled) setIntakeFormDef(null); });
    return () => { cancelled = true; };
  }, [picked, token]);

  const pickedSchema = (() => {
    if (!picked) return null;
    const schema = picked.schema_json && typeof picked.schema_json === 'string'
      ? (() => { try { return JSON.parse(picked.schema_json); } catch { return null; } })()
      : picked.schema_json;
    return schema || null;
  })();

  const submit = async () => {
    if (!picked) return;
    if (!form.title.trim()) {
      setError('Title is required.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const customFieldsOut = intakeFormDef
        ? mapFormValuesToCaseKeys(intakeFormValues, pickedSchema, intakeFormDef)
        : (form.custom_fields || {});
      const body = {
        case_type_id: picked.id,
        title: form.title.trim(),
        description: form.description || null,
        priority: form.priority || 'medium',
        custom_fields_json: customFieldsOut,
      };
      const r = await axios.post(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases`,
        body,
        { headers: authHeaders(token) },
      );
      onCreated(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not create case.');
    } finally {
      setBusy(false);
    }
  };

  const customFields = pickedSchema?.fields || [];

  // Step-2 body, used as the renderBuiltin fallback for `add_case.builtin`.
  // When the workspace authors a custom add_case Layout, granular widgets
  // read the same draft slices (form / intakeFormValues / picked) via
  // useLayoutCtx() and write through setDraft({...patch}).
  const builtinStep2Body = (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'auto' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>Title <span style={{ color: '#ef4444' }}>*</span></label>
          <input
            value={form.title}
            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            style={{ ...inputStyle, width: '100%' }}
            placeholder="Short summary"
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>Description</label>
          <textarea
            value={form.description}
            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
            style={{ ...inputStyle, minHeight: '80px', resize: 'vertical', width: '100%' }}
          />
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>Priority</label>
          <select
            value={form.priority}
            onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
            style={{ ...inputStyle, width: '100%' }}
          >
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
        {intakeFormDef ? (
          <div style={{ marginTop: '6px' }}>
            <XavvyFormRenderer
              token={token}
              definition={intakeFormDef}
              initialValues={intakeFormValues}
              onValuesChange={setIntakeFormValues}
            />
          </div>
        ) : customFields.length > 0 && (
          <>
            <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginTop: '6px' }}>Custom fields</div>
            {customFields.map(f => (
              <SimpleField
                key={f.key}
                field={f}
                value={form.custom_fields[f.key]}
                onChange={(v) => setForm(prev => ({ ...prev, custom_fields: { ...prev.custom_fields, [f.key]: v } }))}
              />
            ))}
          </>
        )}
      </div>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '14px', paddingTop: '12px', borderTop: '1px solid var(--border-color)' }}>
        <button onClick={() => setPicked(null)} style={btnSecondary}>← Pick different type</button>
        <button onClick={onClose} style={btnSecondary}>Cancel</button>
        <button onClick={submit} disabled={busy || !form.title.trim()} style={btnPrimary}>
          {busy ? 'Creating…' : 'Create case'}
        </button>
      </div>
    </div>
  );

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={{ ...modalContent, width: '600px', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
          <h3 style={{ margin: 0, fontSize: '16px' }}>{picked ? `New ${picked.name}` : 'Add a case'}</h3>
          <button onClick={onClose} style={{ ...btnSecondary, padding: '4px 10px' }}>×</button>
        </div>
        {error && <div style={{ background: '#ef444420', color: '#ef4444', padding: '8px 10px', borderRadius: '6px', marginBottom: '12px', fontSize: '12px' }}>{error}</div>}
        {!picked ? (
          types === null ? (
            <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading case types…</div>
          ) : types.length === 0 ? (
            <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>No active case types in this workspace.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {types.map(t => (
                <button
                  key={t.id}
                  onClick={() => setPicked(t)}
                  style={{
                    textAlign: 'left',
                    padding: '12px 14px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-tertiary)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: '14px' }}>{t.icon || '📋'} {t.name}</div>
                  {t.description && <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>{t.description}</div>}
                </button>
              ))}
            </div>
          )
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: '0 1 auto', maxHeight: '60vh' }}>
            <LayoutRenderer
              category="add_case"
              token={token}
              workspaceId={workspaceId}
              currentUser={currentUser}
              role={role}
              caseTypeId={picked.id}
              roleId={roleId || null}
              draft={draft}
              setDraft={setDraft}
              callbacks={{
                onSubmit: submit,
                onCancel: onClose,
                onPickAgain: () => setPicked(null),
                renderBuiltin: () => builtinStep2Body,
              }}
            />
          </div>
        )}
        {!picked && (
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '14px', paddingTop: '12px', borderTop: '1px solid var(--border-color)' }}>
            <button onClick={onClose} style={btnSecondary}>Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}

// Edit mode is gated on whether the current worker has a claimed work item on
// this case. The work item *is* the unit of work — there is no separate
// case-level lock concept on the worker surface. Computed here from the
// task-progress endpoint (each task carries its active_work_item.claimed_by).
function CaseFieldEditor({ field, value, onChange }) {
  const t = (field.type || 'text').toLowerCase();
  const common = { ...inputStyle, width: '100%' };
  if (t === 'textarea') {
    return <textarea value={value ?? ''} onChange={e => onChange(e.target.value)} rows={3} style={common} />;
  }
  if (t === 'number') {
    return <input type="number" value={value ?? ''} onChange={e => onChange(e.target.value === '' ? '' : Number(e.target.value))} style={common} />;
  }
  if (t === 'select' && Array.isArray(field.options)) {
    return (
      <select value={value ?? ''} onChange={e => onChange(e.target.value)} style={common}>
        <option value="">—</option>
        {field.options.map(o => {
          const ov = typeof o === 'object' ? (o.value ?? o.label) : o;
          const ol = typeof o === 'object' ? (o.label ?? o.value) : o;
          return <option key={String(ov)} value={ov}>{ol}</option>;
        })}
      </select>
    );
  }
  if (t === 'checkbox') {
    return <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} />;
  }
  if (t === 'date') {
    return <input type="date" value={value ?? ''} onChange={e => onChange(e.target.value)} style={common} />;
  }
  return <input type="text" value={value ?? ''} onChange={e => onChange(e.target.value)} style={common} />;
}

// ─── Case detail pane ────────────────────────────────────────────────────
function CaseDetail({ token, workspaceId, caseRow, currentUser, onCaseUpdated, onOpenWorkItem, workItemRefreshNonce = 0, onDirtyChange }) {
  const [error, setError] = useState('');
  const [tab, setTab] = useState('overview');
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  // Detail form: when case_type.form_config_json.detail_form_id is set, the
  // Overview tab (read-only and edit) renders the xavvy form instead of the
  // raw schema fields. The form's values are keyed by form-field NAME, not
  // case-field KEY — convert with mapCaseValuesToFormNames / mapFormValuesToCaseKeys.
  const [detailFormDef, setDetailFormDef] = useState(null);

  const schemaFields = caseRow.case_type?.schema_json?.fields || [];
  const caseSchema = caseRow.case_type?.schema_json || null;

  useEffect(() => {
    const cfg = caseRow.case_type?.form_config_json;
    const parsedCfg = typeof cfg === 'string'
      ? (() => { try { return JSON.parse(cfg); } catch { return null; } })()
      : cfg;
    const detailId = parsedCfg?.detail_form_id;
    if (!detailId) { setDetailFormDef(null); return; }
    let cancelled = false;
    axios.get(`${API_BASE_URL}/xavvy-forms/${detailId}`, { headers: authHeaders(token) })
      .then(r => {
        if (cancelled) return;
        const def = r.data?.definition;
        const parsed = typeof def === 'string' ? (() => { try { return JSON.parse(def); } catch { return null; } })() : def;
        setDetailFormDef(parsed || null);
      })
      .catch(() => { if (!cancelled) setDetailFormDef(null); });
    return () => { cancelled = true; };
  }, [caseRow.case_type?.form_config_json, token]);

  // Notify parent of dirty state. Dirty = currently editing AND the form
  // contents differ from the saved case. Parent uses this to render "*" on
  // the tab and gate the close-tab confirm.
  useEffect(() => {
    if (!onDirtyChange) return;
    if (!editing) { onDirtyChange(false); return; }
    const orig = JSON.stringify({
      title: caseRow.title || '',
      description: caseRow.description || '',
      priority: caseRow.priority || 'medium',
      custom_fields: caseRow.custom_fields_json || {},
    });
    const current = JSON.stringify({
      title: editForm.title || '',
      description: editForm.description || '',
      priority: editForm.priority || 'medium',
      custom_fields: editForm.custom_fields || {},
    });
    onDirtyChange(orig !== current);
  }, [editing, editForm, caseRow.title, caseRow.description, caseRow.priority, caseRow.custom_fields_json, onDirtyChange]);

  // Clear dirty when this CaseDetail unmounts (tab closed / case switched).
  useEffect(() => {
    return () => { if (onDirtyChange) onDirtyChange(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStatusUpdated = (updated) => {
    onCaseUpdated({ ...caseRow, ...updated });
  };

  const handleOpenWorkItem = (id, stepName) => {
    if (!onOpenWorkItem) return;
    onOpenWorkItem(
      { id, step_name: stepName },
      { id: caseRow.id, case_number: caseRow.case_number, title: caseRow.title },
    );
  };

  const startEditing = () => {
    setEditForm({
      title: caseRow.title || '',
      description: caseRow.description || '',
      priority: caseRow.priority || 'medium',
      custom_fields: { ...(caseRow.custom_fields_json || {}) },
    });
    setError('');
    setEditing(true);
  };

  const cancelEditing = () => {
    const orig = JSON.stringify({
      title: caseRow.title || '',
      description: caseRow.description || '',
      priority: caseRow.priority || 'medium',
      custom_fields: caseRow.custom_fields_json || {},
    });
    const current = JSON.stringify({
      title: editForm.title || '',
      description: editForm.description || '',
      priority: editForm.priority || 'medium',
      custom_fields: editForm.custom_fields || {},
    });
    if (orig !== current && !window.confirm('Discard unsaved changes?')) return;
    setEditing(false);
    setEditForm({});
    setError('');
  };

  const saveEdits = async () => {
    for (const f of schemaFields) {
      if (f.required) {
        const v = editForm.custom_fields?.[f.key];
        if (v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0)) {
          setError(`"${f.label || f.key}" is required.`);
          return;
        }
      }
    }
    setSaving(true);
    setError('');
    try {
      const patch = {};
      if (editForm.title !== caseRow.title) patch.title = editForm.title;
      if (editForm.description !== caseRow.description) patch.description = editForm.description;
      if (editForm.priority !== caseRow.priority) patch.priority = editForm.priority;
      if (Object.keys(editForm.custom_fields || {}).length > 0
          || Object.keys(caseRow.custom_fields_json || {}).length > 0) {
        patch.custom_fields_json = editForm.custom_fields;
      }
      if (Object.keys(patch).length > 0) {
        const r = await axios.patch(
          `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases/${caseRow.id}`,
          patch,
          { headers: authHeaders(token) },
        );
        onCaseUpdated({ ...caseRow, ...r.data });
      }
      setEditing(false);
      setEditForm({});
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'monospace', fontSize: '11px', color: 'var(--text-secondary)' }}>{caseRow.case_number || caseRow.id}</div>
            <h2 style={{ margin: '4px 0 8px', fontSize: '18px', lineHeight: 1.3 }}>{caseRow.title || '(untitled case)'}</h2>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <StatusBadge status={caseRow.status} />
              <PriorityBadge priority={caseRow.priority} />
              {caseRow.case_type?.name && <Badge color="#8b5cf6">{caseRow.case_type.icon || '📋'} {caseRow.case_type.name}</Badge>}
              {caseRow.current_stage_name && <Badge color="#3b82f6">stage: {caseRow.current_stage_name}</Badge>}
              {caseRow.case_type_id && caseRow.id && (
                <PredictedNextChip
                  sourceType="case"
                  sourceDefId={caseRow.case_type_id}
                  caseId={caseRow.id}
                  refreshKey={workItemRefreshNonce}
                  compact
                />
              )}
            </div>
          </div>
          {!editing && (
            <div style={{ display: 'flex', gap: '6px' }}>
              <button onClick={startEditing} style={btnSecondary}>✎ Edit case</button>
            </div>
          )}
        </div>
        {!editing && (
          <div style={{ marginTop: '10px' }}>
            <StatusChanger token={token} workspaceId={workspaceId} caseRow={caseRow} onUpdated={handleStatusUpdated} />
          </div>
        )}
        {error && <div style={{ background: '#ef444420', color: '#ef4444', padding: '8px 10px', borderRadius: '6px', marginTop: '10px', fontSize: '12px' }}>{error}</div>}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid var(--border-color)', background: 'var(--bg-secondary)', padding: '0 20px' }}>
        {[
          { id: 'overview', label: 'Overview' },
          { id: 'tasks', label: 'Tasks' },
          { id: 'comments', label: 'Comments' },
          { id: 'activity', label: 'Activity' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              background: 'transparent',
              border: 'none',
              borderBottom: `2px solid ${tab === t.id ? 'var(--accent-color)' : 'transparent'}`,
              color: tab === t.id ? 'var(--accent-color)' : 'var(--text-secondary)',
              padding: '10px 14px',
              fontSize: '13px',
              fontWeight: tab === t.id ? 600 : 400,
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
        {tab === 'overview' && !editing && (
          <>
            <DetailField label="Description">
              {caseRow.description || <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>No description.</span>}
            </DetailField>
            {caseRow.created_at && (
              <DetailField label="Created">{new Date(caseRow.created_at).toLocaleString()}</DetailField>
            )}
            {caseRow.sla_resolution_deadline && (
              <DetailField label="Resolution due">{new Date(caseRow.sla_resolution_deadline).toLocaleString()}</DetailField>
            )}
            {detailFormDef ? (
              <DetailField label={`${caseRow.case_type?.icon || '📋'} ${caseRow.case_type?.name || 'Case'} fields`}>
                <XavvyFormRenderer
                  token={token}
                  definition={detailFormDef}
                  initialValues={mapCaseValuesToFormNames(caseRow.custom_fields_json || {}, detailFormDef)}
                  readOnly={true}
                />
              </DetailField>
            ) : (schemaFields.length > 0 || (caseRow.custom_fields_json && Object.keys(caseRow.custom_fields_json).length > 0)) && (() => {
              // Show every field from the case-type schema (including empty
              // ones), then any extra keys present on the case that aren't in
              // the schema. Case-insensitive matching against custom_fields_json
              // mirrors the writeback helper. Empty values render as "—".
              const cf = caseRow.custom_fields_json || {};
              const cfLower = {};
              Object.entries(cf).forEach(([k, v]) => { cfLower[k.toLowerCase()] = { k, v }; });
              const seen = new Set();
              const rows = schemaFields.map(f => {
                const hit = cfLower[(f.key || '').toLowerCase()];
                if (hit) seen.add(hit.k);
                const v = hit ? hit.v : undefined;
                return { key: f.key, label: f.label || f.key, value: v, type: f.type };
              });
              Object.entries(cf).forEach(([k, v]) => {
                if (k.startsWith('_') || seen.has(k)) return;
                rows.push({ key: k, label: k, value: v });
              });
              const fmt = (row) => {
                const v = row.value;
                if (v === undefined || v === null || v === '') return <span style={{ color: 'var(--text-tertiary)' }}>—</span>;
                if (row.type === 'checkbox') return v ? 'Yes' : 'No';
                if (Array.isArray(v)) return v.length === 0 ? <span style={{ color: 'var(--text-tertiary)' }}>—</span> : v.join(', ');
                if (typeof v === 'object') return JSON.stringify(v);
                return String(v);
              };
              return (
                <DetailField label="Custom fields">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {rows.map(row => (
                      <div key={row.key} style={{ display: 'flex', gap: '10px', fontSize: '13px' }}>
                        <span style={{ color: 'var(--text-secondary)', minWidth: '140px' }}>{row.label}</span>
                        <span>{fmt(row)}</span>
                      </div>
                    ))}
                  </div>
                </DetailField>
              );
            })()}
          </>
        )}
        {tab === 'overview' && editing && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', maxWidth: '600px' }}>
            <div>
              <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: '4px' }}>Title</div>
              <input
                type="text"
                value={editForm.title || ''}
                onChange={e => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                style={{ ...inputStyle, width: '100%' }}
              />
            </div>
            <div>
              <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: '4px' }}>Description</div>
              <textarea
                value={editForm.description || ''}
                onChange={e => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                rows={4}
                style={{ ...inputStyle, width: '100%' }}
              />
            </div>
            <div>
              <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: '4px' }}>Priority</div>
              <select
                value={editForm.priority || 'medium'}
                onChange={e => setEditForm(prev => ({ ...prev, priority: e.target.value }))}
                style={{ ...inputStyle, width: '100%' }}
              >
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            {detailFormDef ? (
              <div>
                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginTop: '4px', marginBottom: '8px' }}>
                  {caseRow.case_type?.icon || '📋'} {caseRow.case_type?.name} fields
                </div>
                <XavvyFormRenderer
                  token={token}
                  definition={detailFormDef}
                  initialValues={mapCaseValuesToFormNames(editForm.custom_fields || {}, detailFormDef)}
                  onValuesChange={(vals) => {
                    setEditForm(prev => ({ ...prev, custom_fields: mapFormValuesToCaseKeys(vals, caseSchema, detailFormDef) }));
                  }}
                />
              </div>
            ) : schemaFields.length > 0 && (
              <>
                <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)', marginTop: '4px' }}>
                  {caseRow.case_type?.icon || '📋'} {caseRow.case_type?.name} fields
                </div>
                {schemaFields.map(f => (
                  <div key={f.key}>
                    <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                      {f.label || f.key}{f.required && <span style={{ color: '#ef4444' }}> *</span>}
                    </div>
                    <CaseFieldEditor
                      field={f}
                      value={editForm.custom_fields?.[f.key]}
                      onChange={v => setEditForm(prev => ({ ...prev, custom_fields: { ...prev.custom_fields, [f.key]: v } }))}
                    />
                  </div>
                ))}
              </>
            )}
            <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
              <button disabled={saving} onClick={saveEdits} style={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
              <button disabled={saving} onClick={cancelEditing} style={btnSecondary}>Cancel</button>
            </div>
          </div>
        )}
        {tab === 'tasks' && (
          <TasksTab
            key={workItemRefreshNonce}
            token={token}
            workspaceId={workspaceId}
            caseId={caseRow.id}
            currentUser={currentUser}
            onWorkItemOpen={handleOpenWorkItem}
          />
        )}
        {tab === 'comments' && (
          <CommentsTab token={token} workspaceId={workspaceId} caseId={caseRow.id} />
        )}
        {tab === 'activity' && (
          <ActivityTab token={token} workspaceId={workspaceId} caseId={caseRow.id} />
        )}
      </div>
    </div>
  );
}

function DetailField({ label, children }) {
  return (
    <div style={{ marginBottom: '14px' }}>
      <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '13px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{children}</div>
    </div>
  );
}

// ─── Docuforge inbox tab (operator slice of IDP in the worker shell) ─────
const dfStatusColors = {
  received: '#6b7280',
  classifying: '#3b82f6',
  extracting: '#3b82f6',
  review_required: '#f59e0b',
  qc_required: '#8b5cf6',
  extracted: '#10b981',
  error: '#ef4444',
  discarded: '#6b7280',
};

function DfStatusBadge({ status }) {
  const c = dfStatusColors[status] || '#6b7280';
  return <Badge color={c}>{(status || '').replace(/_/g, ' ')}</Badge>;
}

const DF_CURRENCY_CODES = [
  'USD', 'EUR', 'GBP', 'AUD', 'CAD', 'NZD', 'INR', 'JPY', 'CNY', 'CHF',
  'KRW', 'HKD', 'SGD', 'MXN', 'BRL', 'ZAR', 'SEK', 'NOK', 'DKK', 'PLN',
  'AED', 'TRY', 'ILS', 'RUB', 'THB',
];

function parseDfMoney(v) {
  if (v && typeof v === 'object' && 'amount' in v && 'currency' in v) {
    return { amount: String(v.amount ?? ''), currency: String(v.currency ?? '').toUpperCase() };
  }
  if (typeof v !== 'string') return null;
  const s = v.trim();
  if (!s.startsWith('{') || !s.endsWith('}')) return null;
  try {
    const obj = JSON.parse(s);
    if (obj && typeof obj === 'object' && 'amount' in obj && 'currency' in obj) {
      return { amount: String(obj.amount ?? ''), currency: String(obj.currency ?? '').toUpperCase() };
    }
  } catch (_) { /* not JSON */ }
  return null;
}

function DfFieldEditor({ value, onChange, disabled }) {
  const money = parseDfMoney(value);
  if (!money) {
    return (
      <input
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
        style={{ ...inputStyle, width: '100%' }}
      />
    );
  }
  const update = (next) => onChange(JSON.stringify(next));
  const codes = DF_CURRENCY_CODES.includes(money.currency)
    ? DF_CURRENCY_CODES
    : [money.currency, ...DF_CURRENCY_CODES];
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <input
        type="text"
        inputMode="decimal"
        value={money.amount}
        onChange={e => update({ amount: e.target.value, currency: money.currency })}
        disabled={disabled}
        style={{ ...inputStyle, flex: 1 }}
        placeholder="Amount"
      />
      <select
        value={money.currency}
        onChange={e => update({ amount: money.amount, currency: e.target.value })}
        disabled={disabled}
        style={{ ...inputStyle, width: 90 }}
      >
        {codes.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
    </div>
  );
}

function DocuforgeTab({ token, workspaceId, onOpenDocument }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('review_required,qc_required,extracted');
  const [search, setSearch] = useState('');
  const [total, setTotal] = useState(0);
  const [docTypes, setDocTypes] = useState([]);
  const [uploadDocType, setUploadDocType] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    if (!token || !workspaceId) return;
    setLoading(true);
    setError('');
    try {
      const params = { limit: 100, offset: 0 };
      if (statusFilter) params.status = statusFilter;
      const r = await axios.get(
        `${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/documents`,
        { headers: authHeaders(token), params },
      );
      const items = r.data?.documents || [];
      setDocs(items);
      setTotal(r.data?.total ?? items.length);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load documents.');
    } finally {
      setLoading(false);
    }
  }, [token, workspaceId, statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!token || !workspaceId) return;
    axios.get(
      `${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/doc-types`,
      { headers: authHeaders(token) },
    ).then(r => setDocTypes(r.data?.doc_types || []))
     .catch(() => setDocTypes([]));
  }, [token, workspaceId]);

  const handleUpload = useCallback(async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { setUploadMsg('Pick a file first.'); return; }
    setUploading(true);
    setUploadMsg('');
    try {
      const form = new FormData();
      form.append('file', f);
      const path = uploadDocType
        ? `/api/docuforge/workspaces/${workspaceId}/ingest/${uploadDocType}?sync=true`
        : `/api/docuforge/workspaces/${workspaceId}/ingest?sync=true`;
      const r = await axios.post(`${API_BASE_URL}${path}`, form, {
        headers: { ...authHeaders(token) },
      });
      setUploadMsg(`Uploaded ${f.name} (${r.data?.status || 'received'})`);
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (e) {
      setUploadMsg(`Upload failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setUploading(false);
    }
  }, [token, workspaceId, uploadDocType, load]);

  const filtered = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return docs;
    return docs.filter(d =>
      (d.filename || '').toLowerCase().includes(q) ||
      (d.doc_type_name || '').toLowerCase().includes(q) ||
      (d.doc_type_key || '').toLowerCase().includes(q),
    );
  }, [docs, search]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={subBarStyle}>
        <span style={{ fontSize: '13px', fontWeight: 600 }}>Docuforge</span>
        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{total} document{total === 1 ? '' : 's'}</span>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={inputStyle}
        >
          <option value="review_required,qc_required,extracted">In review + completed</option>
          <option value="review_required,qc_required">In review only</option>
          <option value="extracted">Completed only</option>
          <option value="review_required">Review required</option>
          <option value="qc_required">QC required</option>
          <option value="received,classifying,extracting">In flight</option>
          <option value="error">Errored</option>
          <option value="">All statuses</option>
        </select>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search filename or doc type…"
          style={{ ...inputStyle, minWidth: '180px', flex: 1, maxWidth: '240px' }}
        />
        <button onClick={load} style={btnSecondary}>{loading ? 'Loading…' : 'Refresh'}</button>
      </div>
      <div style={{ ...subBarStyle, borderTop: 0 }}>
        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Add document:</span>
        <input ref={fileRef} type="file" style={{ color: 'var(--text-secondary)', fontSize: '12px' }} />
        <select
          value={uploadDocType}
          onChange={e => setUploadDocType(e.target.value)}
          style={inputStyle}
          title="Pick a doc type or leave blank to auto-classify"
        >
          <option value="">Auto-classify</option>
          {docTypes.map(dt => (
            <option key={dt.id} value={dt.key}>{dt.name || dt.key}</option>
          ))}
        </select>
        <button onClick={handleUpload} disabled={uploading} style={btnPrimary}>
          {uploading ? 'Uploading…' : 'Upload'}
        </button>
        {uploadMsg && (
          <span style={{ fontSize: '12px', color: uploadMsg.startsWith('Upload failed') ? '#ef4444' : 'var(--text-secondary)' }}>
            {uploadMsg}
          </span>
        )}
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: '12px 18px' }}>
        {error && <div style={{ background: '#ef444420', color: '#ef4444', padding: '8px 12px', borderRadius: '6px', fontSize: '12px', marginBottom: '12px' }}>{error}</div>}
        {!loading && filtered.length === 0 && (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>
            No documents match the current filter.
          </div>
        )}
        {filtered.length > 0 && (
          <table style={gridTableStyle}>
            <thead>
              <tr>
                <th style={gridThStyle}>Filename</th>
                <th style={gridThStyle}>Doc type</th>
                <th style={gridThStyle}>Status</th>
                <th style={gridThStyle}>Confidence</th>
                <th style={gridThStyle}>Engine</th>
                <th style={gridThStyle}>Received</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(d => (
                <tr
                  key={d.id}
                  onClick={() => onOpenDocument(d)}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-tertiary)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <td style={gridTdStyle}>{d.filename || d.id.slice(0, 8)}</td>
                  <td style={gridTdStyle}>{d.doc_type_name || d.doc_type_key || '—'}</td>
                  <td style={gridTdStyle}><DfStatusBadge status={d.status} /></td>
                  <td style={gridTdStyle}>{d.confidence_min != null ? `${Math.round(d.confidence_min * 100)}%` : '—'}</td>
                  <td style={gridTdStyle}>{d.engine_used || '—'}</td>
                  <td style={gridTdStyle}>{d.ts_received ? new Date(d.ts_received).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Single-document review tab — edit/save/submit (review + QC paths) ──
function DocumentTab({ token, workspaceId, docId, docHint, onCompleted, onDirtyChange }) {
  const [doc, setDoc] = useState(docHint || null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState({});       // {field_path: edited_value}
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [submittedMsg, setSubmittedMsg] = useState('');
  const [qcNotes, setQcNotes] = useState('');

  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);

  // Fetch the document bytes with the Bearer token, then expose via a blob URL
  // so the iframe can render it without re-authenticating. The blob URL is
  // revoked on unmount or doc switch.
  useEffect(() => {
    if (!token || !workspaceId || !docId) return;
    let revoked = null;
    let cancelled = false;
    axios.get(
      `${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/documents/${docId}/file`,
      { headers: authHeaders(token), responseType: 'blob' },
    ).then(r => {
      if (cancelled) return;
      const url = URL.createObjectURL(r.data);
      revoked = url;
      setPreviewBlobUrl(url);
    }).catch(() => { if (!cancelled) setPreviewBlobUrl(null); });
    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
      setPreviewBlobUrl(null);
    };
  }, [token, workspaceId, docId]);

  const refresh = useCallback(async () => {
    if (!token || !workspaceId || !docId) return;
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(
        `${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/documents/${docId}`,
        { headers: authHeaders(token) },
      );
      setDoc(r.data);
      setDraft({});
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load document.');
    } finally {
      setLoading(false);
    }
  }, [token, workspaceId, docId]);

  useEffect(() => { refresh(); }, [refresh]);

  const isDirty = Object.keys(draft).length > 0 || qcNotes.length > 0;
  useEffect(() => { if (onDirtyChange) onDirtyChange(isDirty); }, [isDirty, onDirtyChange]);

  const isQc = doc && doc.status === 'qc_required';
  const editable = doc && (doc.status === 'review_required' || doc.status === 'extracted' || isQc);

  const valueFor = (f) => {
    if (draft[f.field_path] !== undefined) return draft[f.field_path];
    if (f.corrected_value != null) return f.corrected_value;
    if (f.normalized_value != null) return f.normalized_value;
    return f.raw_value ?? '';
  };

  const setValue = (path, v) => setDraft(prev => ({ ...prev, [path]: v }));

  const save = async (submit) => {
    if (!doc) return;
    const corrections = { ...draft };
    if (submit) setSubmitting(true); else setSaving(true);
    setSubmittedMsg('');
    setError('');
    try {
      const r = await axios.post(
        `${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/documents/${docId}/review`,
        { corrections, submit: !!submit },
        { headers: authHeaders(token) },
      );
      if (submit) {
        setSubmittedMsg(r.data?.amendment ? 'Amendment submitted.' : 'Review submitted.');
        setDraft({});
        if (onCompleted) onCompleted();
        await refresh();
      } else {
        setSavedAt(new Date().toLocaleTimeString());
        await refresh();
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
      setSubmitting(false);
    }
  };

  const submitQc = async (outcome) => {
    if (!doc) return;
    const corrections = { ...draft };
    setSubmitting(true);
    setSubmittedMsg('');
    setError('');
    try {
      const r = await axios.post(
        `${API_BASE_URL}/api/docuforge/workspaces/${workspaceId}/documents/${docId}/qc`,
        { outcome, corrections, notes: qcNotes || null },
        { headers: authHeaders(token) },
      );
      setSubmittedMsg(`QC ${r.data?.outcome || outcome} recorded.`);
      setDraft({});
      setQcNotes('');
      if (onCompleted) onCompleted();
      await refresh();
    } catch (e) {
      setError(e?.response?.data?.detail || 'QC submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !doc) {
    return <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>Loading document…</div>;
  }
  if (!doc) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#ef4444', fontSize: '13px' }}>{error || 'Document unavailable.'}</div>;
  }

  const fields = Array.isArray(doc.fields) ? doc.fields : [];
  const previewable = (doc.mime_type || '').includes('pdf') || (doc.mime_type || '').startsWith('image/');

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={subBarStyle}>
        <span style={{ fontSize: '13px', fontWeight: 600 }}>{doc.filename || doc.id.slice(0, 8)}</span>
        <DfStatusBadge status={doc.status} />
        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
          {doc.doc_type_name || doc.doc_type_key || 'Unclassified'}
        </span>
        {doc.engine_used && <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>· {doc.engine_used}</span>}
        {doc.confidence_min != null && (
          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>· min conf {Math.round(doc.confidence_min * 100)}%</span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
          {savedAt && <span style={{ fontSize: '11px', color: '#10b981' }}>Draft saved {savedAt}</span>}
          {submittedMsg && <span style={{ fontSize: '11px', color: '#10b981' }}>{submittedMsg}</span>}
          {isQc ? (
            <>
              <button onClick={() => submitQc('fail')} disabled={submitting} style={btnDanger}>
                {submitting ? 'Submitting…' : (Object.keys(draft).length ? `QC Fail (${Object.keys(draft).length} fix)` : 'QC Fail')}
              </button>
              <button onClick={() => submitQc('pass')} disabled={submitting} style={btnPrimary}>
                {submitting ? 'Submitting…' : 'QC Pass'}
              </button>
            </>
          ) : editable && (
            <>
              <button onClick={() => save(false)} disabled={!isDirty || saving || submitting} style={btnSecondary}>
                {saving ? 'Saving…' : 'Save draft'}
              </button>
              <button onClick={() => save(true)} disabled={saving || submitting} style={btnPrimary}>
                {submitting ? 'Submitting…' : (doc.status === 'extracted' ? 'Submit amendment' : 'Submit review')}
              </button>
            </>
          )}
        </div>
      </div>
      {error && <div style={{ background: '#ef444420', color: '#ef4444', padding: '8px 12px', fontSize: '12px' }}>{error}</div>}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <div style={{ flex: 1, background: 'var(--bg-tertiary)', overflow: 'auto', borderRight: '1px solid var(--border-color)' }}>
          {previewable && previewBlobUrl ? (
            <iframe
              title="document preview"
              src={previewBlobUrl}
              style={{ border: 'none', width: '100%', height: '100%' }}
            />
          ) : previewable ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>Loading preview…</div>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>
              No inline preview for {doc.mime_type || 'this file'}.
            </div>
          )}
        </div>
        <div style={{ width: '440px', overflow: 'auto', padding: '14px 18px', flexShrink: 0 }}>
          {isQc && (
            <div style={{ background: '#8b5cf620', borderLeft: '3px solid #8b5cf6', padding: '8px 10px', borderRadius: '4px', marginBottom: '14px', fontSize: '12px', color: 'var(--text-secondary)' }}>
              <strong style={{ color: '#8b5cf6' }}>QC sample.</strong> Spot-check the fields below. Use <em>QC Pass</em> if everything is correct, or edit values + <em>QC Fail</em> to flag this doc.
              <textarea
                value={qcNotes}
                onChange={e => setQcNotes(e.target.value)}
                placeholder="QC notes (optional)…"
                rows={2}
                style={{ ...inputStyle, width: '100%', marginTop: '6px', resize: 'vertical', fontFamily: 'inherit' }}
              />
            </div>
          )}
          <h4 style={{ margin: '0 0 10px', fontSize: '13px' }}>Extracted fields ({fields.length})</h4>
          {fields.length === 0 && (
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>No fields extracted yet.</div>
          )}
          {fields.map(f => {
            const conf = f.confidence != null ? Math.round(f.confidence * 100) : null;
            const confColor = conf == null ? '#6b7280' : conf >= 90 ? '#10b981' : conf >= 70 ? '#f59e0b' : '#ef4444';
            return (
              <div key={f.field_path} style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{f.field_path}</span>
                  {conf != null && <Badge color={confColor}>{conf}%</Badge>}
                </div>
                <DfFieldEditor
                  value={valueFor(f)}
                  onChange={v => setValue(f.field_path, v)}
                  disabled={!editable}
                />
                {f.corrected_value != null && draft[f.field_path] === undefined && (
                  <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                    Previously corrected · raw: <span style={{ fontFamily: 'monospace' }}>{f.raw_value}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Bootstrap: auto-load workspaces, pick the first ─────────────────────
export default function CaseClientBootstrap({
  token,
  currentUser,
  allWorkspaces,
  currentWorkspace,
  setCurrentWorkspace,
  setAllWorkspaces,
  initialWorkspaceId,
  onLogout,
  onExit,
}) {
  const [loadState, setLoadState] = useState('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    if (allWorkspaces && allWorkspaces.length > 0) {
      pickWorkspace(allWorkspaces);
      return;
    }
    setLoadState('loading');
    axios.get(`${API_BASE_URL}/workspaces/`, {
      headers: authHeaders(token),
      params: { member_only: true },
    }).then(r => {
      if (cancelled) return;
      const ws = Array.isArray(r.data) ? r.data : [];
      if (setAllWorkspaces) setAllWorkspaces(ws);
      pickWorkspace(ws);
    }).catch(e => {
      if (cancelled) return;
      setError(e?.response?.data?.detail || 'Failed to load your workspaces.');
      setLoadState('error');
    });
    return () => { cancelled = true; };

    function pickWorkspace(ws) {
      if (!ws || ws.length === 0) {
        setLoadState('empty');
        return;
      }
      let chosen = null;
      if (initialWorkspaceId) {
        chosen = ws.find(w => w.id === initialWorkspaceId) || null;
      }
      if (!chosen && currentWorkspace) {
        chosen = ws.find(w => w.id === currentWorkspace.id) || null;
      }
      if (!chosen) {
        const sorted = [...ws].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        chosen = sorted[0];
      }
      if (chosen && (!currentWorkspace || currentWorkspace.id !== chosen.id)) {
        setCurrentWorkspace(chosen);
      }
      setLoadState('ready');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (loadState === 'loading') {
    return (
      <div style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)', color: 'var(--text-secondary)', fontSize: '14px' }}>
        Loading your xavvy Client…
      </div>
    );
  }

  if (loadState === 'error') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)', color: 'var(--text-primary)', gap: '14px', padding: '20px' }}>
        <div style={{ color: '#ef4444', fontSize: '14px' }}>{error}</div>
        <button onClick={onExit} style={btnSecondary}>Back to studio</button>
      </div>
    );
  }

  if (loadState === 'empty') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-primary)', color: 'var(--text-primary)', gap: '14px', padding: '20px', textAlign: 'center' }}>
        <h2 style={{ margin: 0, fontSize: '18px' }}>No xavvy Client workspace</h2>
        <div style={{ color: 'var(--text-secondary)', fontSize: '13px', maxWidth: '420px' }}>
          You don't have access to any workspace yet. Ask an administrator to add you as a member.
        </div>
        {onLogout && <button onClick={onLogout} style={btnSecondary}>Logout</button>}
      </div>
    );
  }

  if (!currentWorkspace) {
    return null;
  }

  return (
    <CaseClientShell
      token={token}
      currentUser={currentUser}
      workspaceId={currentWorkspace.id}
      allWorkspaces={allWorkspaces}
      onChangeWorkspace={(id) => {
        const ws = (allWorkspaces || []).find(w => w.id === id);
        if (ws) setCurrentWorkspace(ws);
      }}
      onLogout={onLogout}
    />
  );
}

// ─── Main shell — ICN-style tabbed MDI ────────────────────────────────────
//
// Layout:
//   ┌── Top bar ─ workspace · role · +Add case · user · logout ──────────┐
//   ├── Left rail ──┬── Tab strip ──────────────────────────────────────┤
//   │ Cases        │ [📋 Cases][📥 SLA queue ×][📁 CASE-001 ×]          │
//   │ In-Baskets   ├──────────────────────────────────────────────────────┤
//   │   👤 My Inbox│  Active tab content (HomeTab / InboxTab / CaseTab)  │
//   │   📥 SLA q.. │                                                      │
//   └──────────────┴──────────────────────────────────────────────────────┘
//
// Each in-basket and each opened case lives in its own closeable tab.
// Inactive tabs stay mounted (display: none) so switching back is instant
// and ongoing fetches/timers (e.g. case lock heartbeat) keep ticking —
// matching ICN's "every workspace stays warm" feel.
function CaseClientShell({ token, currentUser, workspaceId, allWorkspaces, onChangeWorkspace, onLogout }) {
  const [config, setConfig] = useState(null);
  const [globalBranding, setGlobalBranding] = useState({ app_name: 'xavvy', logo_url: null, favicon_url: null, accent_color: null });
  const [activeRole, setActiveRole] = useState(undefined);
  const [showRolePicker, setShowRolePicker] = useState(false);
  const [showAddCase, setShowAddCase] = useState(false);
  const [cases, setCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [casesError, setCasesError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('open,in_progress,under_review');
  // scopeToRole=on sends role_assignment_id so the list only shows cases
  // tagged for the user's active role. Defaulted off — workers see all cases
  // in the workspace and use status/search to narrow. The toggle UI was
  // removed; this stays in state in case we re-introduce it.
  const [scopeToRole, setScopeToRole] = useState(false);
  const [baskets, setBaskets] = useState([]);

  // === Tabs (MDI) ===
  const HOME_TAB = { id: 'home', type: 'home', label: 'Cases', icon: '📋' };
  const [tabs, setTabs] = useState([HOME_TAB]);
  const [activeTabId, setActiveTabId] = useState('home');
  // Set of tab ids whose child surface reports unsaved changes. Updated by
  // CaseTab / WorkItemTab via onDirtyChange. Drives the "*" indicator on the
  // tab strip and the close-tab confirm.
  const [dirtyTabIds, setDirtyTabIds] = useState(() => new Set());
  const setTabDirty = useCallback((id, isDirty) => {
    setDirtyTabIds(prev => {
      const has = prev.has(id);
      if (isDirty === has) return prev;
      const next = new Set(prev);
      if (isDirty) next.add(id); else next.delete(id);
      return next;
    });
  }, []);

  const openTab = useCallback((tab) => {
    setTabs(prev => prev.find(t => t.id === tab.id) ? prev : [...prev, tab]);
    setActiveTabId(tab.id);
  }, []);

  const closeTab = useCallback((id) => {
    setTabs(prev => {
      const idx = prev.findIndex(t => t.id === id);
      if (idx === -1) return prev;
      const next = prev.filter(t => t.id !== id);
      setActiveTabId(curId => {
        if (curId !== id) return curId;
        return next[idx - 1]?.id || next[idx]?.id || 'home';
      });
      return next;
    });
    setDirtyTabIds(prev => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const openCaseTab = useCallback((c, workItemId) => {
    if (!c || !c.id) return;
    openTab({
      id: `case:${c.id}`,
      type: 'case',
      label: c.case_number || c.title || c.id.slice(0, 8),
      icon: '📁',
      caseId: c.id,
      initialCase: c,
      initialWorkItemId: workItemId || null,
    });
  }, [openTab]);

  const openInboxTab = useCallback((basket) => {
    const isPersonal = basket.basket_type === 'personal';
    const icon = basket.icon && basket.icon !== 'inbox' ? basket.icon : (isPersonal ? '👤' : '📥');
    openTab({
      id: `inbox:${basket.id}`,
      type: 'inbox',
      label: basket.name,
      icon,
      basket,
    });
  }, [openTab]);

  const openWorkItemTab = useCallback((wi, caseHint) => {
    if (!wi || !wi.id) return;
    const stepLabel = wi.step_name || 'Step';
    const caseLabel = caseHint?.case_number || (caseHint?.id ? caseHint.id.slice(0, 8) : '');
    openTab({
      id: `workitem:${wi.id}`,
      type: 'workitem',
      label: caseLabel ? `${caseLabel} · ${stepLabel}` : stepLabel,
      icon: '📝',
      workItemId: wi.id,
      caseHint: caseHint || null,
    });
  }, [openTab]);

  const openDocuforgeTab = useCallback(() => {
    openTab({ id: 'docuforge', type: 'docuforge', label: 'Docuforge', icon: '📄' });
  }, [openTab]);

  const openDocumentTab = useCallback((d) => {
    if (!d || !d.id) return;
    openTab({
      id: `document:${d.id}`,
      type: 'document',
      label: d.filename || d.id.slice(0, 8),
      icon: '📄',
      docId: d.id,
      docHint: d,
    });
  }, [openTab]);

  // Bumped whenever a work item completes — propagated down to CaseTab so the
  // Tasks sub-tab inside any open case tab re-fetches its progress data.
  const [workItemRefreshNonce, setWorkItemRefreshNonce] = useState(0);
  const bumpWorkItemRefresh = useCallback(() => setWorkItemRefreshNonce(n => n + 1), []);

  const currentWorkspace = useMemo(
    () => (allWorkspaces || []).find(w => w.id === workspaceId) || null,
    [allWorkspaces, workspaceId],
  );

  // Global branding (xavvy logo / app name / accent / favicon). Public
  // endpoint, no auth needed. Mirrors App.jsx so the standalone
  // /case-client deep-link renders the right brand even without going
  // through the studio shell first.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE_URL}/api/branding`).then(res => {
      if (cancelled) return;
      const b = res.data || {};
      setGlobalBranding({
        app_name: b.app_name || 'xavvy',
        logo_url: b.logo_url || null,
        favicon_url: b.favicon_url || null,
        accent_color: b.accent_color || null,
      });
      if (b.app_name) document.title = `${b.app_name} — xavvy Client`;
      if (b.favicon_url) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
        link.href = b.favicon_url;
      }
      if (b.accent_color) {
        document.documentElement.style.setProperty('--accent-color', b.accent_color);
        document.documentElement.style.setProperty('--primary-color', b.accent_color);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Workspace-scoped branding overrides the global one when set
  // (CaseClientConfig.branding_json: {name?, logo_url?, accent_color?}).
  const brand = useMemo(() => {
    const wsBrand = config && config.branding && typeof config.branding === 'object' ? config.branding : null;
    return {
      name: wsBrand?.name || globalBranding.app_name || 'xavvy',
      logo_url: wsBrand?.logo_url || globalBranding.logo_url || null,
      accent_color: wsBrand?.accent_color || globalBranding.accent_color || null,
    };
  }, [config, globalBranding]);

  // Apply workspace-level accent when present (last writer wins, after global).
  useEffect(() => {
    const wsAccent = config?.branding?.accent_color;
    if (wsAccent) {
      document.documentElement.style.setProperty('--accent-color', wsAccent);
      document.documentElement.style.setProperty('--primary-color', wsAccent);
    }
  }, [config?.branding?.accent_color]);

  useEffect(() => {
    if (!token || !workspaceId) return;
    let cancelled = false;
    setActiveRole(undefined);
    // Reset tabs on workspace switch — old case tabs reference stale IDs
    setTabs([HOME_TAB]);
    setActiveTabId('home');

    // Phase L5 — load custom widget plugins for this workspace. Idempotent;
    // bundles already-loaded for this session won't be re-fetched.
    bootstrapPluginWidgets({ token, workspaceId }).catch(() => {});

    axios.get(`${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/case-client/config`, { headers: authHeaders(token) })
      .then(r => { if (!cancelled) setConfig(r.data); })
      .catch(() => { if (!cancelled) setConfig(null); });

    axios.get(`${API_BASE_URL}/api/casematrix/me/active-case-role`, { headers: authHeaders(token) })
      .then(r => {
        if (cancelled) return;
        const a = r.data?.active;
        if (a && a.workspace_id === workspaceId) setActiveRole(a);
        else setActiveRole(null);
      })
      .catch(() => { if (!cancelled) setActiveRole(null); });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, workspaceId]);

  const loadCases = useCallback(async () => {
    if (!token || !workspaceId) return;
    setCasesLoading(true);
    setCasesError('');
    try {
      const params = {
        status: statusFilter || undefined,
        search: search || undefined,
        sort_by: 'created_at',
        sort_order: 'desc',
        page_size: 100,
      };
      if (scopeToRole && activeRole && activeRole.assignment_id) {
        params.role_assignment_id = activeRole.assignment_id;
      }
      const r = await axios.get(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/cases`,
        { headers: authHeaders(token), params },
      );
      setCases(r.data?.items || []);
    } catch (e) {
      setCasesError(e?.response?.data?.detail || 'Failed to load cases.');
    } finally {
      setCasesLoading(false);
    }
  }, [token, workspaceId, statusFilter, search, scopeToRole, activeRole]);

  useEffect(() => { loadCases(); }, [loadCases]);

  const loadBaskets = useCallback(async () => {
    if (!token || !workspaceId) return;
    try {
      const r = await axios.get(
        `${API_BASE_URL}/api/casematrix/workspaces/${workspaceId}/in-baskets`,
        { headers: authHeaders(token) },
      );
      setBaskets(r.data || []);
    } catch {
      setBaskets([]);
    }
  }, [token, workspaceId]);

  useEffect(() => { loadBaskets(); }, [loadBaskets]);

  const visibleBaskets = useMemo(() => {
    return baskets.filter(ib => {
      const roleIds = Array.isArray(ib.case_role_ids) ? ib.case_role_ids : [];
      // Unrestricted (open) baskets are always visible.
      if (roleIds.length === 0) return true;
      // Role-restricted baskets need an active role that matches.
      if (!activeRole || !activeRole.role_id) return false;
      return roleIds.includes(activeRole.role_id);
    });
  }, [baskets, activeRole]);

  useEffect(() => {
    if (activeRole === null) setShowRolePicker(true);
  }, [activeRole]);

  // Cross-tab updates: when a CaseTab updates a case (lock/status), reflect
  // it in the home grid so badges stay accurate without a full refresh.
  const handleCaseUpdated = useCallback((updated) => {
    if (!updated) return;
    setCases(prev => prev.map(c => c.id === updated.id ? { ...c, ...updated } : c));
  }, []);

  const onRolePicked = (active) => {
    setShowRolePicker(false);
    if (!active) {
      setActiveRole(null);
      return;
    }
    setActiveRole(active);
  };

  useEffect(() => {
    if (!workspaceId) return;
    const target = `/case-client/${workspaceId}`;
    if (window.location.pathname !== target) {
      window.history.replaceState({}, '', target);
    }
  }, [workspaceId]);

  const addCaseEnabled = !config || config.add_case_enabled !== false;

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {brand.logo_url
            ? <img src={brand.logo_url} alt={brand.name} style={{ height: '24px', maxWidth: '100px', objectFit: 'contain' }} />
            : <img src={logo} alt={brand.name} style={{ height: '24px', width: '24px' }} />}
          <span style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-primary)' }}>{brand.name}</span>
          <span style={{ fontSize: '13px', color: 'var(--text-secondary)', borderLeft: '1px solid var(--border-color)', paddingLeft: '10px' }}>xavvy Client</span>
          {(allWorkspaces || []).length > 1 ? (
            <select
              value={workspaceId || ''}
              onChange={e => onChangeWorkspace && onChangeWorkspace(e.target.value)}
              style={inputStyle}
            >
              {(allWorkspaces || []).map(w => (
                <option key={w.id} value={w.id}>{w.name}</option>
              ))}
            </select>
          ) : (
            <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{currentWorkspace?.name}</span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <button onClick={() => setShowRolePicker(true)} style={btnSecondary}>
            {activeRole && activeRole.role_name ? `Role: ${activeRole.role_name}` : 'Pick role'}
          </button>
          {addCaseEnabled && <button onClick={() => setShowAddCase(true)} style={btnPrimary}>+ Add case</button>}
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{currentUser?.username || currentUser?.email}</span>
          {onLogout && <button onClick={onLogout} style={btnSecondary}>Logout</button>}
        </div>
      </div>

      {config && config.enabled === false && (
        <div style={{ padding: '8px 18px', background: '#f59e0b20', color: '#f59e0b', fontSize: '12px', borderBottom: '1px solid var(--border-color)' }}>
          This workspace's xavvy Client is currently disabled by an administrator. You can still preview cases here.
        </div>
      )}

      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <LeftNavRail
          baskets={visibleBaskets}
          activeTabId={activeTabId}
          onOpenHome={() => setActiveTabId('home')}
          onOpenBasket={openInboxTab}
          onRefresh={loadBaskets}
          docuforgeEnabled={!!config?.docuforge_enabled}
          onOpenDocuforge={openDocuforgeTab}
        />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--bg-primary)' }}>
          <TabStrip
            tabs={tabs}
            activeTabId={activeTabId}
            dirtyIds={dirtyTabIds}
            onActivate={setActiveTabId}
            onClose={closeTab}
          />
          <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
            {tabs.map(t => (
              <div
                key={t.id}
                style={{
                  display: t.id === activeTabId ? 'flex' : 'none',
                  flexDirection: 'column',
                  position: 'absolute',
                  inset: 0,
                  overflow: 'hidden',
                }}
              >
                {t.type === 'home' && (
                  <LayoutRenderer
                    category="home"
                    token={token}
                    workspaceId={workspaceId}
                    currentUser={currentUser}
                    role={activeRole}
                    roleId={activeRole?.assignment_id || null}
                    callbacks={{
                      onOpenCase: (c) => openCaseTab(c),
                      renderBuiltin: () => (
                        <HomeTab
                          token={token}
                          workspaceId={workspaceId}
                          cases={cases}
                          loading={casesLoading}
                          error={casesError}
                          currentUser={currentUser}
                          search={search}
                          setSearch={setSearch}
                          statusFilter={statusFilter}
                          setStatusFilter={setStatusFilter}
                          onRefresh={loadCases}
                          onOpenCase={(c) => openCaseTab(c)}
                        />
                      ),
                    }}
                  />
                )}
                {t.type === 'inbox' && (
                  <InboxTab
                    token={token}
                    workspaceId={workspaceId}
                    basket={t.basket}
                    onOpenWorkItem={openWorkItemTab}
                  />
                )}
                {t.type === 'case' && (
                  <CaseTab
                    token={token}
                    workspaceId={workspaceId}
                    caseId={t.caseId}
                    initialCase={t.initialCase}
                    currentUser={currentUser}
                    role={activeRole}
                    roleId={activeRole?.assignment_id || null}
                    onCaseUpdated={handleCaseUpdated}
                    onOpenWorkItem={openWorkItemTab}
                    workItemRefreshNonce={workItemRefreshNonce}
                    onDirtyChange={(d) => setTabDirty(t.id, d)}
                  />
                )}
                {t.type === 'workitem' && (
                  <WorkItemTab
                    token={token}
                    workspaceId={workspaceId}
                    workItemId={t.workItemId}
                    caseHint={t.caseHint}
                    currentUser={currentUser}
                    role={activeRole}
                    roleId={activeRole?.assignment_id || null}
                    onOpenCase={(c) => openCaseTab(c)}
                    onCompleted={() => { closeTab(t.id); bumpWorkItemRefresh(); }}
                    onDirtyChange={(d) => setTabDirty(t.id, d)}
                  />
                )}
                {t.type === 'docuforge' && (
                  <DocuforgeTab
                    token={token}
                    workspaceId={workspaceId}
                    onOpenDocument={openDocumentTab}
                  />
                )}
                {t.type === 'document' && (
                  <DocumentTab
                    token={token}
                    workspaceId={workspaceId}
                    docId={t.docId}
                    docHint={t.docHint}
                    onCompleted={() => bumpWorkItemRefresh()}
                    onDirtyChange={(d) => setTabDirty(t.id, d)}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {showRolePicker && (
        <RolePicker
          token={token}
          workspaceId={workspaceId}
          currentRoleId={activeRole?.assignment_id}
          onSelect={onRolePicked}
          onClose={() => setShowRolePicker(false)}
        />
      )}
      {showAddCase && (
        <AddCaseModal
          token={token}
          workspaceId={workspaceId}
          currentUser={currentUser}
          role={activeRole}
          roleId={activeRole?.assignment_id || null}
          onClose={() => setShowAddCase(false)}
          onCreated={(newCase) => {
            setShowAddCase(false);
            setCases(prev => [newCase, ...prev]);
            openCaseTab(newCase);
          }}
        />
      )}
    </div>
  );
}
